import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line } from 'recharts';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Map as MapIcon,
  Receipt as ReceiptIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Engineering as EngineeringIcon,
  Slideshow as SlideshowIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Search as SearchIcon,
  Computer as ComputerIcon,
  DataUsage as DataUsageIcon,
  BarChart as BarChartIcon,
  Public as PublicIcon,
  Cloud as CloudIcon,
  NetworkCheck as NetworkCheckIcon,
  DataObject as DataObjectIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import SalesPitchDeck from './SalesPitchDeck';

interface ComparisonDashboardProps {
  analysisResult: any;
  onReset: () => void;
}


const ComparisonDashboard: React.FC<ComparisonDashboardProps> = ({ analysisResult, onReset }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showSalesDeck, setShowSalesDeck] = useState(false);
  const [salesDeck, setSalesDeck] = useState(null);
  const [isGeneratingDeck, setIsGeneratingDeck] = useState(false);
  const [deckError, setDeckError] = useState('');
  
  // Research section state
  const [researchData, setResearchData] = React.useState<any>(null);
  const [performanceData, setPerformanceData] = React.useState<any>(null);
  const [activeResearchTab, setActiveResearchTab] = React.useState('enterprise-compute');

  // Fetch research data
  React.useEffect(() => {
    const fetchResearchData = async () => {
      try {
        const [skuRes, perfRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/sku-research`),
          fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/performance-metrics`)
        ]);
        const skuData = await skuRes.json();
        const perfData = await perfRes.json();
        setResearchData(skuData.research_data);
        setPerformanceData(perfData.performance_data);
      } catch (error) {
        console.error('Failed to fetch research data:', error);
      }
    };
    fetchResearchData();
  }, []);

  const { oci_mappings, claude_analysis, cost_comparison, bom, aws_monthly_cost, oci_monthly_cost, monthly_savings, annual_savings, savings_percentage } = analysisResult;

  const costComparisonData = oci_mappings.map((mapping: any) => ({
    service: mapping.aws_service.replace('Amazon ', ''),
    'AWS Cost': mapping.aws_cost,
    'OCI Cost': mapping.estimated_oci_cost || 0,
  }));

  const savingsData = [
    { name: 'Current AWS Cost', value: aws_monthly_cost || cost_comparison?.aws_monthly_cost || 0, color: '#FF8042' },
    { name: 'Projected OCI Cost', value: oci_monthly_cost || cost_comparison?.oci_monthly_cost || 0, color: '#00C49F' },
  ];

  const generateSalesPitchDeck = React.useCallback(async () => {
    setIsGeneratingDeck(true);
    setDeckError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/generate-sales-deck`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          aws_bill_data: {
            total_cost: aws_monthly_cost || cost_comparison?.aws_monthly_cost || 0,
            bill_name: analysisResult.bill_name || 'Customer Bill',
            services: oci_mappings.map((mapping: any) => ({
              service_name: mapping.aws_service,
              total_cost: mapping.aws_cost,
              usage_details: [],
              regions: ['us-east-1'],
              usage_types: []
            }))
          },
          oci_mappings: oci_mappings,
          claude_analysis: claude_analysis
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate sales deck');
      }

      const data = await response.json();
      setSalesDeck(data.sales_deck);
      // Don't auto-open modal - user will click "See Final Deck" button
    } catch (error) {
      setDeckError(error instanceof Error ? error.message : 'Failed to generate sales deck');
    } finally {
      setIsGeneratingDeck(false);
    }
  }, [aws_monthly_cost, cost_comparison, analysisResult, oci_mappings, claude_analysis]);

  // TEMPORARY: Disable auto-generation of sales deck to prevent infinite loops
  // TODO: Re-enable when Claude API and backend issues are resolved
  // React.useEffect(() => {
  //   if (analysisResult && !salesDeck && !isGeneratingDeck) {
  //     generateSalesPitchDeck();
  //   }
  // }, [analysisResult, salesDeck, isGeneratingDeck, generateSalesPitchDeck]);

  const renderOverview = () => {
    // Analyze customer's current AWS services to determine appropriate differentiators
    const hasDatabase = oci_mappings.some((mapping: any) => 
      mapping.aws_service.toLowerCase().includes('rds') || 
      mapping.aws_service.toLowerCase().includes('database') ||
      mapping.aws_service.toLowerCase().includes('dynamodb') ||
      mapping.aws_service.toLowerCase().includes('redshift')
    );
    
    const hasCompute = oci_mappings.some((mapping: any) => 
      mapping.aws_service.toLowerCase().includes('ec2') ||
      mapping.aws_service.toLowerCase().includes('compute') ||
      mapping.aws_service.toLowerCase().includes('lambda')
    );
    
    const hasStorage = oci_mappings.some((mapping: any) => 
      mapping.aws_service.toLowerCase().includes('s3') ||
      mapping.aws_service.toLowerCase().includes('ebs') ||
      mapping.aws_service.toLowerCase().includes('storage')
    );
    
    const hasNetworking = oci_mappings.some((mapping: any) => 
      mapping.aws_service.toLowerCase().includes('cloudfront') ||
      mapping.aws_service.toLowerCase().includes('vpc') ||
      mapping.aws_service.toLowerCase().includes('load') ||
      mapping.aws_service.toLowerCase().includes('route')
    );
    
    // Determine primary differentiator based on what customer actually uses
    let primaryDifferentiator = "bare metal performance";
    let performanceMetric = "25% better performance";
    
    if (hasDatabase) {
      primaryDifferentiator = "autonomous database technology";
      performanceMetric = "3x faster queries and 80% less DBA work";
    } else if (hasCompute) {
      primaryDifferentiator = "true bare metal cloud";
      performanceMetric = "25% better performance with zero hypervisor tax";
    } else if (hasStorage) {
      primaryDifferentiator = "2x IOPS performance with free egress";
      performanceMetric = "480K IOPS vs 240K on AWS";
    } else if (hasNetworking) {
      primaryDifferentiator = "integrated WAF and DDoS protection";
      performanceMetric = "50 Gbps networking vs 25 Gbps";
    }

    return (
    <Box>
      {/* AI-Generated Executive Summary */}
      <Card sx={{ p: 2, mb: 2, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.200' }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 700, mb: 1.5 }}>
          <PsychologyIcon sx={{ mr: 1, fontSize: 28 }} />
          AI Insights - Executive Summary
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 2, lineHeight: 1.5, fontWeight: 500 }}>
          {claude_analysis?.executive_summary || `Your current AWS spend of $${aws_monthly_cost ? aws_monthly_cost.toLocaleString() : '2,547,890'}/month presents an exceptional opportunity to save $${monthly_savings ? monthly_savings.toLocaleString() : '914,196'}/month (${(savings_percentage || 35.9).toFixed(1)}%), achieve ${performanceMetric}, and gain ${primaryDifferentiator} by migrating to Oracle Cloud Infrastructure.`}
        </Typography>
        
        {/* AI-Generated Value Proposition or Fallback */}
        {claude_analysis?.value_proposition ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ color: 'grey.800', fontWeight: 600, mb: 1.5 }}>
              AI Value Proposition
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'success.main', fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon sx={{ mr: 1, fontSize: 18 }} /> Cost Savings
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {claude_analysis.value_proposition.cost_savings}
                </Typography>
                
                <Typography variant="subtitle2" sx={{ color: 'info.main', fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center' }}>
                  <SpeedIcon sx={{ mr: 1, fontSize: 18 }} /> Performance Gains
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {claude_analysis.value_proposition.performance_gains}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'warning.main', fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center' }}>
                  <EngineeringIcon sx={{ mr: 1, fontSize: 18 }} /> Operational Efficiency
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {claude_analysis.value_proposition.operational_efficiency}
                </Typography>
                
                <Typography variant="subtitle2" sx={{ color: 'error.main', fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center' }}>
                  <PublicIcon sx={{ mr: 1, fontSize: 18 }} /> Competitive Advantage
                </Typography>
                <Typography variant="body2">
                  {claude_analysis.value_proposition.competitive_advantage}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle1" sx={{ color: 'grey.800', fontWeight: 600, mb: 1.5 }}>
              Value Proposition
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'success.main', fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon sx={{ mr: 1, fontSize: 18 }} /> Cost Savings
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Immediate <strong>${monthly_savings ? monthly_savings.toLocaleString() : '914,196'}/month</strong> savings through OCI's superior price/performance and included services
                </Typography>
            
            <Typography variant="subtitle2" sx={{ color: 'info.main', fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center' }}>
              <SpeedIcon sx={{ mr: 1, fontSize: 18 }} /> Performance Gains
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {hasCompute && (
                <>
                  <strong>25% faster compute</strong> with VM.Standard.E5.Flex (no hypervisor overhead) vs EC2 instances<br/>
                </>
              )}
              {hasDatabase && (
                <>
                  <strong>3x faster query performance</strong> with Autonomous Database vs RDS<br/>
                </>
              )}
              {hasStorage && (
                <>
                  <strong>2x IOPS</strong> (480K vs 240K) with OCI Block Volume vs EBS<br/>
                </>
              )}
              {hasNetworking && (
                <>
                  <strong>150% better networking</strong> (50 Gbps vs 25 Gbps) with integrated security<br/>
                </>
              )}
              {!hasCompute && !hasDatabase && !hasStorage && !hasNetworking && (
                <>
                  <strong>Superior price/performance</strong> across all OCI services vs AWS equivalents
                </>
              )}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="subtitle2" sx={{ color: 'warning.main', fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center' }}>
              <EngineeringIcon sx={{ mr: 1, fontSize: 18 }} /> Operational Efficiency
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>80% reduction in DBA tasks</strong> with Autonomous Database self-management<br/>
              <strong>Eliminate hypervisor management</strong> with true bare metal instances<br/>
              <strong>Zero surprise egress fees</strong> with free regional data transfer
            </Typography>
            
            <Typography variant="subtitle2" sx={{ color: 'secondary.main', fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center' }}>
              <MemoryIcon sx={{ mr: 1, fontSize: 18 }} /> Technology Advantage
            </Typography>
            <Typography variant="body2">
              <strong>Latest AMD EPYC 9J14 processors</strong> (50% better price/performance vs AWS m6a)<br/>
              <strong>World's only autonomous database</strong> with patented self-managing technology<br/>
              <strong>True bare metal cloud</strong> at virtual machine pricing
            </Typography>
          </Box>
            </Box>
          </Box>
        )}
      </Card>
      
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
        <Box sx={{ flex: 1 }}>
          <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', minHeight: 200 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrendingUpIcon sx={{ fontSize: 28, color: 'white' }} />
              </Box>
            </Box>
            <Typography variant="h6" gutterBottom sx={{ color: 'grey.900', fontWeight: 600 }}>Monthly Savings</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
              ${(monthly_savings || cost_comparison?.monthly_savings || 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="success.700" sx={{ fontWeight: 500 }}>
              {(savings_percentage || cost_comparison?.savings_percentage || 0).toFixed(1)}% reduction
            </Typography>
          </Card>
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', minHeight: 200 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AssessmentIcon sx={{ fontSize: 28, color: 'white' }} />
              </Box>
            </Box>
            <Typography variant="h6" gutterBottom sx={{ color: 'grey.900', fontWeight: 600 }}>Annual Savings</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              ${(annual_savings || cost_comparison?.annual_savings || 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="primary.700" sx={{ fontWeight: 500 }}>
              First year projection
            </Typography>
          </Card>
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: 'warning.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <EngineeringIcon sx={{ fontSize: 28, color: 'white' }} />
              </Box>
            </Box>
            <Typography variant="h6" gutterBottom sx={{ color: 'grey.900', fontWeight: 600 }}>Migration Timeline</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
              {claude_analysis?.migration_complexity || 'Medium'}
            </Typography>
            <Typography variant="body2" color="warning.700" sx={{ fontWeight: 500 }}>
              {claude_analysis?.migration_timeline_estimate || '4-6 months phased migration, starting with compute and storage workloads'}
            </Typography>
          </Card>
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        <Box sx={{ flex: { lg: 2 } }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Cost Comparison by Service</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={costComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="service" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value?.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="AWS Cost" fill="#FF8042" />
                  <Bar dataKey="OCI Cost" fill="#00C49F" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { lg: 1 } }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Cost Breakdown</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                  <Pie
                    data={savingsData}
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                    label={false}
                  >
                    {savingsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value?.toLocaleString()}`} />
                  <Legend 
                    formatter={(value, entry) => `${value}: $${entry.payload?.value?.toLocaleString()}`}
                    wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      </Stack>
    </Box>
    );
  };

  const renderServiceMappings = () => (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h6" gutterBottom>AWS to OCI Service Mappings</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>AWS Service</strong></TableCell>
                <TableCell><strong>OCI Service</strong></TableCell>
                <TableCell align="right"><strong>AWS Cost</strong></TableCell>
                <TableCell align="right"><strong>OCI Cost</strong></TableCell>
                <TableCell align="right"><strong>Savings</strong></TableCell>
                <TableCell align="center"><strong>Confidence</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {oci_mappings.map((mapping: any, index: number) => (
                <React.Fragment key={index}>
                  <TableRow hover>
                    <TableCell>{mapping.aws_service}</TableCell>
                    <TableCell>
                      {mapping.oci_service} - {mapping.oci_product}
                      {mapping.compute_shape_mappings && mapping.compute_shape_mappings.length > 0 && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {mapping.compute_shape_mappings.length} specific shape mapping(s)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">${mapping.aws_cost?.toLocaleString()}</TableCell>
                    <TableCell align="right">${mapping.estimated_oci_cost?.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                      ${(mapping.aws_cost - (mapping.estimated_oci_cost || 0))?.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${(mapping.mapping_confidence * 100).toFixed(0)}%`}
                        color={mapping.mapping_confidence >= 0.9 ? 'success' : 
                               mapping.mapping_confidence >= 0.7 ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                  
                  {/* Show detailed compute shape mappings if available */}
                  {mapping.compute_shape_mappings && mapping.compute_shape_mappings.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 1, bgcolor: 'grey.50' }}>
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Specific Compute Shape Mappings:
                          </Typography>
                          <Stack spacing={1}>
                            {mapping.compute_shape_mappings.map((shape: any, shapeIndex: number) => (
                              <Box key={shapeIndex} sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                p: 1,
                                bgcolor: 'white',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.200'
                              }}>
                                <Chip 
                                  label={shape.aws_instance_type}
                                  color="warning"
                                  size="small"
                                  sx={{ minWidth: 80 }}
                                />
                                <Typography variant="body2" sx={{ mx: 1 }}>→</Typography>
                                <Chip 
                                  label={shape.oci_shape}
                                  color="primary"
                                  size="small"
                                  sx={{ minWidth: 120 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {shape.ocpus} OCPUs, {shape.memory_gb}GB RAM
                                </Typography>
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                  ~${shape.cost_estimate?.toLocaleString()}/month
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderBOM = () => (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Bill of Materials (BOM)</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Item ID</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Product</strong></TableCell>
                <TableCell align="right"><strong>Monthly Cost</strong></TableCell>
                <TableCell align="right"><strong>Annual Cost</strong></TableCell>
                <TableCell align="center"><strong>Priority</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bom.map((item: any, index: number) => (
                <TableRow 
                  key={index} 
                  hover
                  sx={item.item_id === 'BOM-TOTAL' ? { backgroundColor: 'grey.100', fontWeight: 'bold' } : {}}
                >
                  <TableCell>{item.item_id}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>{item.product}</TableCell>
                  <TableCell align="right">${item.monthly_cost?.toLocaleString()}</TableCell>
                  <TableCell align="right">${item.annual_cost?.toLocaleString()}</TableCell>
                  <TableCell align="center">
                    {item.migration_priority && (
                      <Chip 
                        label={item.migration_priority}
                        color={item.migration_priority === 'High' ? 'error' : 
                               item.migration_priority === 'Medium' ? 'warning' : 'success'}
                        size="small"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderPerformanceAnalysis = () => {
    // Calculate performance metrics
    const totalAWSCost = costComparisonData.reduce((sum: number, item: any) => sum + item['AWS Cost'], 0);
    const totalOCICost = costComparisonData.reduce((sum: number, item: any) => sum + item['OCI Cost'], 0);
    const performanceData = costComparisonData.map((item: any) => ({
      service: item.service,
      'Cost per GB': item['AWS Cost'] / 100, // Simplified metric
      'Performance Score': Math.random() * 100, // Would be real performance data
      'Efficiency Rating': ((item['AWS Cost'] - item['OCI Cost']) / item['AWS Cost']) * 100
    }));

    const monthlyTrendData = [
      { month: 'Jan', AWS: totalAWSCost * 0.9, OCI: totalOCICost * 0.9 },
      { month: 'Feb', AWS: totalAWSCost * 0.95, OCI: totalOCICost * 0.93 },
      { month: 'Mar', AWS: totalAWSCost, OCI: totalOCICost },
      { month: 'Apr', AWS: totalAWSCost * 1.05, OCI: totalOCICost * 1.02 },
      { month: 'May', AWS: totalAWSCost * 1.1, OCI: totalOCICost * 1.05 },
      { month: 'Jun', AWS: totalAWSCost * 1.15, OCI: totalOCICost * 1.08 }
    ];

    return (
      <Box>
        {/* Performance Metrics Cards */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
            <SpeedIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Performance Gain</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main' }}>
              +{Math.round(((totalAWSCost - totalOCICost) / totalAWSCost) * 100)}%
            </Typography>
            <Typography variant="body2" color="info.700">Expected improvement</Typography>
          </Card>
          
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
            <MemoryIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Memory Efficiency</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
              +35%
            </Typography>
            <Typography variant="body2" color="success.700">OCI Flex Shapes advantage</Typography>
          </Card>
          
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
            <DataUsageIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Network Performance</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
              2x
            </Typography>
            <Typography variant="body2" color="warning.700">Faster networking included</Typography>
          </Card>
        </Stack>

        {/* Performance Charts */}
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ mb: 4 }}>
          <Card elevation={2} sx={{ flex: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Cost Efficiency Trend</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value?.toLocaleString()}`} />
                  <Legend />
                  <Line type="monotone" dataKey="AWS" stroke="#FF8042" strokeWidth={3} />
                  <Line type="monotone" dataKey="OCI" stroke="#00C49F" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="service" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="Efficiency Rating" fill="#00C49F" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Stack>

        {/* Detailed Performance Analysis */}
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Performance Comparison Details</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Metric</strong></TableCell>
                    <TableCell><strong>AWS Current</strong></TableCell>
                    <TableCell><strong>OCI Projected</strong></TableCell>
                    <TableCell><strong>Improvement</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Compute Performance (IOPS)</TableCell>
                    <TableCell>25,000 IOPS</TableCell>
                    <TableCell>45,000 IOPS</TableCell>
                    <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>+80%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Network Throughput</TableCell>
                    <TableCell>10 Gbps</TableCell>
                    <TableCell>25 Gbps</TableCell>
                    <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>+150%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Storage Bandwidth</TableCell>
                    <TableCell>1.5 GB/s</TableCell>
                    <TableCell>3.2 GB/s</TableCell>
                    <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>+113%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Memory Bandwidth</TableCell>
                    <TableCell>85 GB/s</TableCell>
                    <TableCell>120 GB/s</TableCell>
                    <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>+41%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderComputeAnalysis = () => {
    // Extract compute-specific data
    const computeServices = oci_mappings.filter((mapping: any) => 
      mapping.aws_service.includes('EC2') || 
      mapping.aws_service.includes('Compute') ||
      mapping.aws_service.includes('Lightsail') ||
      mapping.oci_service.includes('Compute') ||
      (mapping.compute_shape_mappings && mapping.compute_shape_mappings.length > 0)
    );

    const computeShapeData = computeServices.flatMap((service: any) => 
      service.compute_shape_mappings || []
    );

    const shapeCategories = computeShapeData.reduce((acc: any, shape: any) => {
      const category = shape.oci_shape.includes('Flex') ? 'Flexible Shapes' :
                     shape.oci_shape.includes('BM.') ? 'Bare Metal' :
                     shape.oci_shape.includes('GPU') ? 'GPU Instances' : 'Standard VMs';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    const categoryData = Object.entries(shapeCategories).map(([name, value]) => ({
      name, value
    }));

    return (
      <Box>
        {/* Compute Overview Cards */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'info.50' }}>
            <ComputerIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Total Instances</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main' }}>
              {computeShapeData.reduce((sum: number, shape: any) => sum + (Number(shape.instance_count) || 1), 0)}
            </Typography>
            <Typography variant="body2" color="info.700">
              {computeServices.filter((s: any) => s.aws_service.includes('Lightsail')).length > 0 && 
               computeServices.filter((s: any) => s.aws_service.includes('EC2')).length > 0 
                ? `Lightsail + EC2 instances`
                : computeServices.filter((s: any) => s.aws_service.includes('Lightsail')).length > 0 
                  ? `Lightsail instances`
                  : `EC2 instances`}
            </Typography>
          </Card>

          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'primary.50' }}>
            <ComputerIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Total OCPUs</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {computeShapeData.reduce((sum: number, shape: any) => sum + (Number(shape.ocpus) || 0), 0)}
            </Typography>
            <Typography variant="body2" color="primary.700">Across all instances</Typography>
          </Card>
          
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'secondary.50' }}>
            <MemoryIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Total Memory</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main' }}>
              {computeShapeData.reduce((sum: number, shape: any) => sum + (Number(shape.memory_gb) || 0), 0)} GB
            </Typography>
            <Typography variant="body2" color="secondary.700">Total RAM allocation</Typography>
          </Card>
          
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'success.50' }}>
            <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Compute Savings</Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                color: 'success.main',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                lineHeight: 1.2,
                wordBreak: 'break-all',
                overflowWrap: 'break-word',
                hyphens: 'auto'
              }}
            >
              ${computeServices.reduce((sum: number, service: any) => sum + (service.aws_cost - service.estimated_oci_cost), 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="success.700">Monthly compute savings</Typography>
          </Card>
        </Stack>

        {/* Compute Shape Distribution */}
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ mb: 4 }}>
          <Card elevation={2} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>OCI Shape Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#0088FE', '#00C49F', '#FFBB28', '#FF8042'][index % 4]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ flex: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Instance Type Mappings</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>AWS Instance</strong></TableCell>
                      <TableCell><strong>Count</strong></TableCell>
                      <TableCell><strong>OCI Shape</strong></TableCell>
                      <TableCell align="right"><strong>OCPUs</strong></TableCell>
                      <TableCell align="right"><strong>Memory (GB)</strong></TableCell>
                      <TableCell align="right"><strong>Est. Cost</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {computeShapeData.slice(0, 10).map((shape: any, index: number) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip label={shape.aws_instance_type} color="warning" size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={shape.instance_count || 1} 
                            color="info" 
                            size="small" 
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={shape.oci_shape} color="primary" size="small" />
                        </TableCell>
                        <TableCell align="right">{shape.ocpus}</TableCell>
                        <TableCell align="right">{shape.memory_gb}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          ${shape.cost_estimate?.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Stack>

        {/* Detailed Compute Recommendations */}
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Compute Optimization Recommendations</Typography>
            <Stack spacing={2}>
              <Alert severity="info">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  💡 <strong>Flex Shapes Advantage:</strong> OCI's flexible shapes allow you to customize CPU and memory independently, 
                  potentially saving 20-40% compared to fixed AWS instance types.
                </Typography>
              </Alert>
              <Alert severity="success">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  🚀 <strong>Performance Boost:</strong> OCI's latest E4 shapes deliver up to 25% better price-performance 
                  compared to equivalent AWS instances.
                </Typography>
              </Alert>
              <Alert severity="warning">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  ⚡ <strong>Bare Metal Option:</strong> For high-performance workloads, OCI offers bare metal instances 
                  with no virtualization overhead and included network bandwidth.
                </Typography>
              </Alert>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const [currentSlide, setCurrentSlide] = React.useState(0);

  const renderSalesDeckCarousel = () => {
    // Format currency amounts based on size for better readability
    const formatCurrency = (amount: number | null | undefined, options?: { includeFrequency?: boolean }) => {
      if (!amount || amount === 0) return options?.includeFrequency ? '$0/month' : '$0';
      
      const absAmount = Math.abs(amount);
      
      if (absAmount >= 1000000) {
        return `$${(amount / 1000000).toFixed(1)}M${options?.includeFrequency ? '/month' : ''}`;
      } else if (absAmount >= 1000) {
        return `$${(amount / 1000).toFixed(0)}K${options?.includeFrequency ? '/month' : ''}`;
      } else {
        return `$${Math.round(amount)}${options?.includeFrequency ? '/month' : ''}`;
      }
    };

    // Show message if no sales deck is generated yet
    const defaultSlides = [
      {
        title: "Strategic Cloud Transformation Opportunity",
        subtitle: "Why Industry Leaders Choose Oracle Cloud Infrastructure",
        content: (
          <Box>
            {/* Hero Statement - Pitch Deck Style */}
            <Box sx={{ textAlign: 'center', mb: 3, p: 4, bgcolor: 'primary.50', borderRadius: 2 }}>
              <Typography component="div" sx={{ 
                color: 'primary.main', 
                fontWeight: 900, 
                mb: 2,
                fontSize: '96px !important',
                lineHeight: '80px',
                textShadow: '0 8px 16px rgba(0,0,0,0.3)',
                letterSpacing: '-3px',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                display: 'block',
                overflow: 'visible'
              }}>
                ${annual_savings ? '$' + (annual_savings / 1000000).toFixed(2) + 'M' : 'N/A'}
              </Typography>
              <Typography variant="h6" sx={{ 
                color: 'primary.main', 
                fontWeight: 600, 
                mb: 2,
                fontSize: '1.25rem',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                Annual Savings
              </Typography>
              <Typography variant="h6" sx={{ color: 'grey.700', mb: 2 }}>
                Transform your {cost_comparison?.aws_monthly_cost ? '$' + (cost_comparison.aws_monthly_cost / 1000).toFixed(0) + 'K' : 'AWS'} monthly cloud spend into competitive advantage
              </Typography>
            </Box>

            {/* Key Value Props - Condensed */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} color="info.dark" sx={{ mb: 1 }}>Performance</Typography>
                <Typography variant="body2">Bare metal at VM prices<br/>25% better performance</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} color="success.dark" sx={{ mb: 1 }}>Autonomous</Typography>
                <Typography variant="body2">Self-managing database<br/>80% less DBA work</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>Cost Savings</Typography>
                <Typography variant="body2">{cost_comparison?.savings_percentage ? cost_comparison.savings_percentage.toFixed(1) + '% reduction' : 'Cost reduction'}<br/>{formatCurrency(monthly_savings, { includeFrequency: true })} savings</Typography>
              </Box>
            </Box>

            {/* Social Proof */}
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 2, border: '2px solid', borderColor: 'success.200' }}>
              <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 700, mb: 1 }}>
                430,000+ customers modernized with Oracle Cloud
              </Typography>
              <Typography variant="body1" color="success.dark" sx={{ fontWeight: 500 }}>
                Including 100 of the Fortune 500 • Zero-risk migration with Oracle's proven methodology
              </Typography>
            </Box>
          </Box>
        )
      },
      {
        title: "Compute Migration Overview",
        subtitle: `${oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('lightsail') || m.aws_service.toLowerCase().includes('ec2')).length || 'Multiple'} AWS compute services → OCI Flexible Shapes`,
        content: (
          <Box>
            {/* Hero Impact Numbers */}
            <Box sx={{ textAlign: 'center', mb: 3, p: 3, bgcolor: 'success.50', borderRadius: 2, border: '2px solid', borderColor: 'success.200' }}>
              <Typography variant="h2" sx={{ color: 'success.dark', fontWeight: 900, mb: 1 }}>
                {formatCurrency(monthly_savings)}
              </Typography>
              <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 600, mb: 1, textTransform: 'uppercase' }}>
                Monthly Compute Savings
              </Typography>
              <Typography variant="body1" sx={{ color: 'grey.700' }}>
                29% cost reduction with 25% better performance
              </Typography>
            </Box>

            {/* Key Migration Stats */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="info.dark">{oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('lightsail') || m.aws_service.toLowerCase().includes('ec2')).length || 0}</Typography>
                <Typography variant="body2" fontWeight={600}>Compute Services</Typography>
                <Typography variant="caption">Lightsail + EC2 workloads</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="primary.dark">{formatCurrency(cost_comparison?.oci_monthly_cost)}</Typography>
                <Typography variant="body2" fontWeight={600}>Target OCI Cost</Typography>
                <Typography variant="caption">vs {formatCurrency(cost_comparison?.aws_monthly_cost)} AWS</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="secondary.dark">3.3</Typography>
                <Typography variant="body2" fontWeight={600}>Months to ROI</Typography>
                <Typography variant="caption">Break-even timeline</Typography>
              </Box>
            </Box>

            {/* Core Value Props */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ p: 2, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                <Typography variant="h6" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>
                  True Bare Metal Performance
                </Typography>
                <Typography variant="body2">
                  • Zero hypervisor overhead<br/>
                  • 25% faster execution<br/>
                  • Bare metal at VM prices
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
                <Typography variant="h6" fontWeight={700} color="info.dark" sx={{ mb: 1 }}>
                  Flexible Shapes Advantage
                </Typography>
                <Typography variant="body2">
                  • Custom CPU/memory ratios<br/>
                  • Right-size workloads precisely<br/>
                  • Eliminate over-provisioning
                </Typography>
              </Box>
            </Box>

            {/* Migration Path */}
            <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                Recommended Migration Path
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="warning.dark">Current AWS</Typography>
                  <Typography variant="body2">m6i, c6i, r6i instances</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: 'primary.main', mx: 2 }}>→</Typography>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="success.dark">Target OCI</Typography>
                  <Typography variant="body2">E5.Flex, A2.Flex shapes</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )
      },
      {
        title: "Compute Migration Details",
        subtitle: "Technical specifications and performance comparison",
        content: (
          <Box>
            {/* Detailed Comparison */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Card sx={{ p: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.300' }}>
                <Typography variant="h6" color="warning.dark" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                  Current AWS Setup
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Instance Types:</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {/* Show actual AWS instances from bill */}
                  {oci_mappings.filter((m: any) => 
                    m.aws_service?.toLowerCase().includes('lightsail') || 
                    m.aws_service?.toLowerCase().includes('ec2') ||
                    m.aws_service?.toLowerCase().includes('elastic compute')
                  ).slice(0, 5).map((mapping: any, idx: number) => (
                    <span key={idx}>
                      • {mapping.compute_shape_mappings?.[0]?.aws_instance_type || 
                         (mapping.aws_service?.includes('Lightsail') ? 
                           `Lightsail bundles (${mapping.compute_shape_mappings?.length || 1} types)` : 
                           mapping.aws_service
                         )}
                      {idx < Math.min(4, oci_mappings.filter((m: any) => 
                        m.aws_service?.toLowerCase().includes('lightsail') || 
                        m.aws_service?.toLowerCase().includes('ec2') ||
                        m.aws_service?.toLowerCase().includes('elastic compute')
                      ).length - 1) && <br/>}
                    </span>
                  ))}
                  {oci_mappings.filter((m: any) => 
                    m.aws_service?.toLowerCase().includes('lightsail') || 
                    m.aws_service?.toLowerCase().includes('ec2') ||
                    m.aws_service?.toLowerCase().includes('elastic compute')
                  ).length === 0 && (
                    <>
                      • Lightsail 0.5GB Bundle<br/>
                      • Lightsail 1GB Bundle<br/>
                      • Lightsail 2GB Bundle<br/>
                      • Lightsail 4GB Bundle<br/>
                      • EC2 t2.micro & t3.small
                    </>
                  )}
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Limitations:</Typography>
                <Typography variant="body2">
                  • Hypervisor overhead (5-8%)<br/>
                  • Fixed CPU/memory ratios<br/>
                  • Complex pricing tiers
                </Typography>
              </Card>

              <Card sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.300' }}>
                <Typography variant="h6" color="success.dark" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                  Target OCI Setup
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Recommended Shapes:</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {/* Show actual OCI shapes based on Lightsail mappings */}
                  {oci_mappings.filter((m: any) => 
                    m.aws_service?.toLowerCase().includes('lightsail') || 
                    m.aws_service?.toLowerCase().includes('ec2')
                  ).slice(0, 3).map((mapping: any, idx: number) => (
                    <span key={idx}>
                      • {mapping.compute_shape_mappings?.[0]?.oci_shape || 'VM.Standard.E4.Flex'}
                      {mapping.compute_shape_mappings?.[0]?.instance_count > 1 && 
                        ` (${mapping.compute_shape_mappings[0].instance_count}x instances)`
                      }
                      {idx < Math.min(2, oci_mappings.filter((m: any) => 
                        m.aws_service?.toLowerCase().includes('lightsail') || 
                        m.aws_service?.toLowerCase().includes('ec2')
                      ).length - 1) && <br/>}
                    </span>
                  ))}
                  {oci_mappings.filter((m: any) => 
                    m.aws_service?.toLowerCase().includes('lightsail') || 
                    m.aws_service?.toLowerCase().includes('ec2')
                  ).length === 0 && (
                    <>
                      • VM.Standard.E4.Flex (1-2 OCPUs)<br/>
                      • Customizable CPU/memory ratios<br/>
                      • Always-Free tier eligible
                    </>
                  )}
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Advantages:</Typography>
                <Typography variant="body2">
                  • Flexible CPU/memory sizing<br/>
                  • Always-Free tier available<br/>
                  • Predictable hourly pricing
                </Typography>
              </Card>
            </Box>

            {/* Performance Metrics */}
            <Card sx={{ p: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                Performance & Cost Impact
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2, textAlign: 'center' }}>
                <Box>
                  <Typography variant="h5" color="success.dark" fontWeight={700}>{Math.round((monthly_savings / aws_monthly_cost) * 100)}%</Typography>
                  <Typography variant="body2" fontWeight={600}>Cost Reduction</Typography>
                </Box>
                <Box>
                  <Typography variant="h5" color="info.dark" fontWeight={700}>25%</Typography>
                  <Typography variant="body2" fontWeight={600}>Performance Gain</Typography>
                </Box>
                <Box>
                  <Typography variant="h5" color="warning.dark" fontWeight={700}>{formatCurrency(annual_savings)}</Typography>
                  <Typography variant="body2" fontWeight={600}>Annual Savings</Typography>
                </Box>
                <Box>
                  <Typography variant="h5" color="secondary.dark" fontWeight={700}>15%</Typography>
                  <Typography variant="body2" fontWeight={600}>Extra Optimization</Typography>
                </Box>
              </Box>
            </Card>

            {/* Migration Benefits */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Key Migration Benefits:</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
              <Box sx={{ p: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>OCI VM.Standard.E4.Flex Shapes</Typography>
                <Typography variant="body2">Right-sized compute for Lightsail workloads • 25% better price/performance • AMD EPYC processors</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>Flexible Shapes Technology</Typography>
                <Typography variant="body2">Customize CPU/memory independently • Scale from 1-4 OCPUs for small workloads • Pay for exactly what you need</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>Always-Free Tier Eligible</Typography>
                <Typography variant="body2">Up to 4 OCPUs and 24GB RAM free forever • Ideal for development workloads • No time limits</Typography>
              </Box>
            </Box>
          </Box>
        )
      },
      {
        title: "Storage Migration Overview",
        subtitle: "28.8 PB storage migration with 50% cost reduction",
        content: (
          <Box>
            {/* Hero Impact Numbers */}
            <Box sx={{ textAlign: 'center', mb: 3, p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '2px solid', borderColor: 'primary.200' }}>
              <Typography variant="h2" sx={{ color: 'primary.dark', fontWeight: 900, mb: 1 }}>
                $201K
              </Typography>
              <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 600, mb: 1, textTransform: 'uppercase' }}>
                Monthly Storage Savings
              </Typography>
              <Typography variant="body1" sx={{ color: 'grey.700' }}>
                50% cost reduction with 100% higher IOPS performance
              </Typography>
            </Box>

            {/* Key Storage Stats */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="warning.dark">28.8</Typography>
                <Typography variant="body2" fontWeight={600}>PB Total Storage</Typography>
                <Typography variant="caption">19.3 PB Object + 9.5 PB Block</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="success.dark">$190K</Typography>
                <Typography variant="body2" fontWeight={600}>Target OCI Cost</Typography>
                <Typography variant="caption">vs $391K AWS</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="info.dark">480K</Typography>
                <Typography variant="body2" fontWeight={600}>IOPS Performance</Typography>
                <Typography variant="caption">vs 240K AWS</Typography>
              </Box>
            </Box>

            {/* Storage Comparison */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ p: 2, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                <Typography variant="h6" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>
                  Current AWS Storage
                </Typography>
                <Typography variant="body2">
                  • S3: $157K/month (19.3 PB)<br/>
                  • EBS: $235K/month (9.5 PB)<br/>
                  • Complex IOPS pricing<br/>
                  • High egress charges
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                <Typography variant="h6" fontWeight={700} color="success.dark" sx={{ mb: 1 }}>
                  Target OCI Storage
                </Typography>
                <Typography variant="body2">
                  • Object: $89K/month (43% savings)<br/>
                  • Block: $100K/month (57% savings)<br/>
                  • Higher IOPS included<br/>
                  • Free regional egress
                </Typography>
              </Box>
            </Box>

            {/* Key Benefits */}
            <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                Storage Migration Benefits
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.dark" fontWeight={700}>100%</Typography>
                  <Typography variant="body2" fontWeight={600}>Higher IOPS</Typography>
                  <Typography variant="caption">No extra charges</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="info.dark" fontWeight={700}>FREE</Typography>
                  <Typography variant="body2" fontWeight={600}>Regional Egress</Typography>
                  <Typography variant="caption">Eliminate AWS fees</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.dark" fontWeight={700}>$2.4M</Typography>
                  <Typography variant="body2" fontWeight={600}>Annual Savings</Typography>
                  <Typography variant="caption">50% cost reduction</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )
      },
      {
        title: "Networking & CDN Migration",
        subtitle: "Enhanced Global Performance with Cost Optimization",
        content: (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>AWS Networking → OCI Networking Migration</Typography>
            
            {/* Current AWS Networking */}
            <Card sx={{ p: 3, mb: 3, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
              <Typography variant="h6" color="warning.dark" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <CloudIcon sx={{ mr: 1, fontSize: 18 }} /> Current AWS
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>CloudFront CDN:</Typography>
                  <Typography variant="body2">400TB monthly transfer</Typography>
                  <Typography variant="body2">2B HTTPS requests</Typography>
                  <Typography variant="h6" color="warning.dark">$45,679/month</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>Load Balancing:</Typography>
                  <Typography variant="body2">Application Load Balancer</Typography>
                  <Typography variant="body2">Complex LCU pricing</Typography>
                  <Typography variant="body2">Separate SSL costs</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>Data Transfer:</Typography>
                  <Typography variant="body2">High egress charges</Typography>
                  <Typography variant="body2">Inter-AZ transfer fees</Typography>
                  <Typography variant="body2">Unpredictable costs</Typography>
                </Box>
              </Box>
            </Card>

            {/* OCI Networking Target */}
            <Card sx={{ p: 3, mb: 3, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
              <Typography variant="h6" color="success.dark" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <NetworkCheckIcon sx={{ mr: 1, fontSize: 18 }} /> Move to OCI
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>Web Application Acceleration:</Typography>
                  <Typography variant="body2">Same 400TB + WAF included</Typography>
                  <Typography variant="body2">DDoS protection included</Typography>
                  <Typography variant="h6" color="success.dark">$29,550/month</Typography>
                  <Typography variant="caption" color="success.dark">35% savings</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>Load Balancer:</Typography>
                  <Typography variant="body2">SSL termination included</Typography>
                  <Typography variant="body2">Simple per-hour pricing</Typography>
                  <Typography variant="body2">Global load balancing</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>Data Transfer:</Typography>
                  <Typography variant="body2">Free within region</Typography>
                  <Typography variant="body2">No inter-AD charges</Typography>
                  <Typography variant="body2">Predictable costs</Typography>
                </Box>
              </Box>
            </Card>

            {/* Top 3 Benefits */}
            <Typography variant="h6" sx={{ mb: 2 }}>Top 3 Networking Benefits:</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
              <Box sx={{ p: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>1. Integrated Security (WAF + DDoS)</Typography>
                <Typography variant="body2">No additional WAF costs • Built-in DDoS protection • Save $194K annually • $1.1M over 5 years</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'primary.50', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>2. 150% Better Network Performance</Typography>
                <Typography variant="body2">50 Gbps vs 25 Gbps networking • Lower latency • Enhanced global backbone</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'secondary.50', borderLeft: '4px solid', borderColor: 'secondary.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>3. Predictable Bandwidth Pricing</Typography>
                <Typography variant="body2">No surprise egress charges • Free intra-region transfer • Eliminate bill shock</Typography>
              </Box>
            </Box>
          </Box>
        )
      },
      {
        title: "Database & Other Services Migration",
        subtitle: "Autonomous Technology & Service Consolidation",
        content: (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>Databases & Remaining Services Migration</Typography>
            
            {/* Database Migration */}
            <Card sx={{ p: 3, mb: 3, bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.300' }}>
              <Typography variant="h6" sx={{ color: 'grey.800', fontWeight: 600, mb: 3 }}>Database Migration</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <Card sx={{ p: 3, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
                  <Typography variant="subtitle1" fontWeight={600} color="warning.dark" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <CloudIcon sx={{ mr: 1, fontSize: 18 }} /> Current AWS
                  </Typography>
                  <Typography variant="body2">RDS: $345,679/month</Typography>
                  <Typography variant="body2">DynamoDB: $67,890/month</Typography>
                  <Typography variant="body2">Manual management overhead</Typography>
                  <Typography variant="body2">Separate NoSQL/SQL systems</Typography>
                </Card>
                <Card sx={{ p: 3, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                  <Typography variant="subtitle1" fontWeight={600} color="success.dark" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <DataObjectIcon sx={{ mr: 1, fontSize: 18 }} /> Move to OCI
                  </Typography>
                  <Typography variant="body2">Autonomous Database: $267,278/month</Typography>
                  <Typography variant="body2">Self-managing, self-securing</Typography>
                  <Typography variant="body2">Unified SQL + JSON workloads</Typography>
                  <Typography variant="body2">80% reduction in DBA tasks</Typography>
                </Card>
              </Box>
            </Card>

            {/* Other Services */}
            <Card sx={{ p: 3, mb: 3, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
              <Typography variant="h6" color="info.dark" fontWeight={600} sx={{ mb: 2 }}>Additional Services Migration</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>Lambda → Functions:</Typography>
                  <Typography variant="body2">$45,679 → $36,886</Typography>
                  <Typography variant="caption" color="success.main">19% savings</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>EKS → OKE:</Typography>
                  <Typography variant="body2">$34,568 → $27,914</Typography>
                  <Typography variant="caption" color="success.main">19% savings</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>SageMaker → Compute:</Typography>
                  <Typography variant="body2">$89,012 → $63,421</Typography>
                  <Typography variant="caption" color="success.main">29% savings</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>EMR + Redshift:</Typography>
                  <Typography variant="body2">$135,690 → $104,175</Typography>
                  <Typography variant="caption" color="success.main">23% savings</Typography>
                </Box>
              </Box>
            </Card>

            {/* Top 3 Benefits */}
            <Typography variant="h6" sx={{ mb: 2 }}>Top 3 Database & Services Benefits:</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
              <Box sx={{ p: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>1. Autonomous Database Technology</Typography>
                <Typography variant="body2">World's first self-managing database • 80% DBA workload reduction • Save $1.5M annually in ops costs</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'primary.50', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>2. Consolidated Service Portfolio</Typography>
                <Typography variant="body2">Reduce vendor complexity • Unified SQL+NoSQL • Save $377K/year in service consolidation</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'secondary.50', borderLeft: '4px solid', borderColor: 'secondary.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>3. Machine Learning Integration</Typography>
                <Typography variant="body2">Native ML in Autonomous Database • No data movement • 40% faster model training</Typography>
              </Box>
            </Box>
          </Box>
        )
      },
      {
        title: "ROI Analysis",
        subtitle: "Financial Impact & Justification", 
        content: (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>Cost Savings Projection</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3, mb: 4 }}>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'info.50' }}>
                <Typography variant="subtitle1" color="info.main" fontWeight={600}>Monthly Savings</Typography>
                <Typography variant="h4" sx={{ color: 'info.dark' }}>
                  ${monthly_savings ? monthly_savings.toLocaleString() : '914,196'}
                </Typography>
                <Typography variant="caption" color="info.dark">Per month</Typography>
              </Card>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'success.50' }}>
                <Typography variant="subtitle1" color="success.main" fontWeight={600}>Annual Savings</Typography>
                <Typography variant="h4" sx={{ color: 'success.dark' }}>
                  ${annual_savings ? '$' + (annual_savings / 1000000).toFixed(2) + 'M' : 'N/A'}
                </Typography>
                <Typography variant="caption" color="success.dark">Per year</Typography>
              </Card>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'warning.50' }}>
                <Typography variant="subtitle1" color="warning.main" fontWeight={600}>5-Year Savings</Typography>
                <Typography variant="h4" sx={{ color: 'warning.dark' }}>
                  ${annual_savings ? (annual_savings * 5.5).toLocaleString() : '60.2M'}
                </Typography>
                <Typography variant="caption" color="warning.dark">Over 5 years</Typography>
              </Card>
            </Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Value Beyond Cost Savings:</Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>25% performance improvement with bare metal</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Reduced operational complexity</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Enhanced security with autonomous capabilities</Typography>
              <Typography component="li">Access to world-class Oracle support</Typography>
            </Box>
          </Box>
        )
      },
      {
        title: "Next Steps",
        subtitle: "Migration Roadmap & Timeline",
        content: (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>Recommended Migration Path</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
              <Card sx={{ p: 3, border: '2px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                <Typography variant="h6" color="primary.main" fontWeight={600}>Phase 1: Quick Wins (Week 1-2)</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Technical assessment of top 3 services (EC2, RDS, S3)
                  <br />
                  • Proof-of-concept migration for dev/test environment  
                  <br />
                  • Oracle Cloud Credits evaluation ($50K available)
                </Typography>
              </Card>
              <Card sx={{ p: 3, border: '1px solid', borderColor: 'secondary.main', bgcolor: 'secondary.50' }}>
                <Typography variant="h6" color="secondary.main" fontWeight={600}>Phase 2: Pilot Migration (Week 3-8)</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Migrate non-critical workloads representing 20% of spend
                  <br />
                  • Validate performance and cost projections
                  <br />
                  • Train team on OCI console and tools
                </Typography>
              </Card>
              <Card sx={{ p: 3, border: '1px solid', borderColor: 'success.main', bgcolor: 'success.50' }}>
                <Typography variant="h6" color="success.main" fontWeight={600}>Phase 3: Full Migration (Month 3-4)</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Production workload migration with zero downtime
                  <br />
                  • Realize full ${monthly_savings ? monthly_savings.toLocaleString() : '914,196'}/month savings
                  <br />
                  • Ongoing optimization and autonomous features adoption
                </Typography>
              </Card>
            </Box>
          </Box>
        )
      }
    ];

    // Use detailed slides with dynamic data from actual bill analysis
    const slides = [
      {
        title: "Strategic Cloud Transformation Opportunity",
        subtitle: "Why Industry Leaders Choose Oracle Cloud Infrastructure",
        content: (
          <Box>
            {/* Hero Statement - Pitch Deck Style */}
            <Box sx={{ textAlign: 'center', mb: 3, p: 4, bgcolor: 'primary.50', borderRadius: 2 }}>
              <Typography component="div" sx={{ 
                color: 'primary.main', 
                fontWeight: 900, 
                mb: 2,
                fontSize: '96px !important',
                lineHeight: '80px',
                textShadow: '0 8px 16px rgba(0,0,0,0.3)',
                letterSpacing: '-3px',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                display: 'block',
                overflow: 'visible'
              }}>
                {formatCurrency(annual_savings)}
              </Typography>
              <Typography variant="h6" sx={{ 
                color: 'primary.main', 
                fontWeight: 600, 
                mb: 2,
                fontSize: '1.25rem',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                Annual Savings
              </Typography>
              <Typography variant="h6" sx={{ color: 'grey.700', mb: 2 }}>
                Transform your {cost_comparison?.aws_monthly_cost ? '$' + (cost_comparison.aws_monthly_cost / 1000).toFixed(0) + 'K' : 'AWS'} monthly cloud spend into competitive advantage
              </Typography>
            </Box>

            {/* Key Value Props - Condensed */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} color="info.dark" sx={{ mb: 1 }}>Performance</Typography>
                <Typography variant="body2">Bare metal at VM prices<br/>25% better performance</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} color="success.dark" sx={{ mb: 1 }}>Autonomous</Typography>
                <Typography variant="body2">Self-managing database<br/>80% less DBA work</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>Cost Savings</Typography>
                <Typography variant="body2">{cost_comparison?.savings_percentage ? cost_comparison.savings_percentage.toFixed(1) + '% reduction' : 'Cost reduction'}<br/>{formatCurrency(monthly_savings, { includeFrequency: true })} savings</Typography>
              </Box>
            </Box>

            {/* Social Proof */}
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 2, border: '2px solid', borderColor: 'success.200' }}>
              <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 700, mb: 1 }}>
                430,000+ customers modernized with Oracle Cloud
              </Typography>
              <Typography variant="body1" color="success.dark" sx={{ fontWeight: 500 }}>
                Including 100 of the Fortune 500 • Zero-risk migration with Oracle's proven methodology
              </Typography>
            </Box>
          </Box>
        )
      },
      {
        title: "Compute Migration Overview",
        subtitle: `${oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('lightsail') || m.aws_service.toLowerCase().includes('ec2')).length || 'Multiple'} AWS compute services → OCI Flexible Shapes`,
        content: (
          <Box>
            {/* Hero Impact Numbers */}
            <Box sx={{ textAlign: 'center', mb: 3, p: 3, bgcolor: 'success.50', borderRadius: 2, border: '2px solid', borderColor: 'success.200' }}>
              <Typography variant="h2" sx={{ color: 'success.dark', fontWeight: 900, mb: 1 }}>
                {formatCurrency(monthly_savings)}
              </Typography>
              <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 600, mb: 1, textTransform: 'uppercase' }}>
                Monthly Compute Savings
              </Typography>
              <Typography variant="body1" sx={{ color: 'grey.700' }}>
                {cost_comparison?.savings_percentage ? cost_comparison.savings_percentage.toFixed(1) + '% cost reduction with 25% better performance' : '29% cost reduction with 25% better performance'}
              </Typography>
            </Box>

            {/* Key Migration Stats */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="info.dark">{oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('lightsail') || m.aws_service.toLowerCase().includes('ec2')).length || 0}</Typography>
                <Typography variant="body2" fontWeight={600}>Compute Services</Typography>
                <Typography variant="caption">Lightsail + EC2 workloads</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="primary.dark">{formatCurrency(cost_comparison?.oci_monthly_cost)}</Typography>
                <Typography variant="body2" fontWeight={600}>Target OCI Cost</Typography>
                <Typography variant="caption">vs {formatCurrency(cost_comparison?.aws_monthly_cost)} AWS</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="secondary.dark">3.3</Typography>
                <Typography variant="body2" fontWeight={600}>Months to ROI</Typography>
                <Typography variant="caption">OCI vs AWS</Typography>
              </Box>
            </Box>

            {/* Key Principles */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ p: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>Flexible Shapes</Typography>
                <Typography variant="body2">Scale CPU and memory independently • Pay for exactly what you need</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>Bare Metal Performance</Typography>
                <Typography variant="body2">Zero hypervisor tax • Dedicated hardware • Consistent performance</Typography>
              </Box>
            </Box>

            {/* Migration Path */}
            <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                Recommended Migration Path
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="warning.dark">Current AWS</Typography>
                  <Typography variant="body2">m6i, c6i, r6i instances</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: 'primary.main', mx: 2 }}>→</Typography>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="success.dark">Target OCI</Typography>
                  <Typography variant="body2">E5.Flex, A2.Flex shapes</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )
      },
      {
        title: "Compute Migration Details",
        subtitle: "Technical specifications and performance comparison",
        content: (
          <Box>
            {/* Detailed Comparison */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Card sx={{ p: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.300' }}>
                <Typography variant="h6" color="warning.dark" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                  Current AWS Setup
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Instance Types:</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {/* Show actual AWS instances from bill */}
                  {oci_mappings.filter((m: any) => 
                    m.aws_service?.toLowerCase().includes('lightsail') || 
                    m.aws_service?.toLowerCase().includes('ec2') ||
                    m.aws_service?.toLowerCase().includes('elastic compute')
                  ).slice(0, 5).map((mapping: any, idx: number) => (
                    <span key={idx}>
                      • {mapping.compute_shape_mappings?.[0]?.aws_instance_type || 
                         (mapping.aws_service?.includes('Lightsail') ? 
                           `Lightsail bundles (${mapping.compute_shape_mappings?.length || 1} types)` : 
                           mapping.aws_service
                         )}
                      {idx < Math.min(4, oci_mappings.filter((m: any) => 
                        m.aws_service?.toLowerCase().includes('lightsail') || 
                        m.aws_service?.toLowerCase().includes('ec2') ||
                        m.aws_service?.toLowerCase().includes('elastic compute')
                      ).length - 1) && <br/>}
                    </span>
                  ))}
                  {oci_mappings.filter((m: any) => 
                    m.aws_service?.toLowerCase().includes('lightsail') || 
                    m.aws_service?.toLowerCase().includes('ec2') ||
                    m.aws_service?.toLowerCase().includes('elastic compute')
                  ).length === 0 && (
                    <>
                      • Lightsail 0.5GB Bundle<br/>
                      • Lightsail 1GB Bundle<br/>
                      • Lightsail 2GB Bundle<br/>
                      • Lightsail 4GB Bundle<br/>
                      • EC2 t2.micro & t3.small
                    </>
                  )}
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Limitations:</Typography>
                <Typography variant="body2">
                  • Hypervisor overhead (5-8%)<br/>
                  • Fixed CPU/memory ratios<br/>
                  • Complex pricing tiers
                </Typography>
              </Card>

              <Card sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.300' }}>
                <Typography variant="h6" color="success.dark" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                  Target OCI Setup
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Recommended Shapes:</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {/* Show actual OCI shapes based on Lightsail mappings */}
                  {oci_mappings.filter((m: any) => 
                    m.aws_service?.toLowerCase().includes('lightsail') || 
                    m.aws_service?.toLowerCase().includes('ec2')
                  ).slice(0, 3).map((mapping: any, idx: number) => (
                    <span key={idx}>
                      • {mapping.compute_shape_mappings?.[0]?.oci_shape || 'VM.Standard.E4.Flex'}
                      {mapping.compute_shape_mappings?.[0]?.instance_count > 1 && 
                        ` (${mapping.compute_shape_mappings[0].instance_count}x instances)`
                      }
                      {idx < Math.min(2, oci_mappings.filter((m: any) => 
                        m.aws_service?.toLowerCase().includes('lightsail') || 
                        m.aws_service?.toLowerCase().includes('ec2')
                      ).length - 1) && <br/>}
                    </span>
                  ))}
                  {oci_mappings.filter((m: any) => 
                    m.aws_service?.toLowerCase().includes('lightsail') || 
                    m.aws_service?.toLowerCase().includes('ec2')
                  ).length === 0 && (
                    <>
                      • VM.Standard.E4.Flex (1-2 OCPUs)<br/>
                      • Customizable CPU/memory ratios<br/>
                      • Always-Free tier eligible
                    </>
                  )}
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Advantages:</Typography>
                <Typography variant="body2">
                  • Flexible CPU/memory sizing<br/>
                  • Always-Free tier available<br/>
                  • Predictable hourly pricing
                </Typography>
              </Card>
            </Box>

            {/* Performance Metrics */}
            <Card sx={{ p: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                Performance & Cost Impact
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2, textAlign: 'center' }}>
                <Box>
                  <Typography variant="h5" color="success.dark" fontWeight={700}>{cost_comparison?.savings_percentage ? cost_comparison.savings_percentage.toFixed(0) + '%' : '29%'}</Typography>
                  <Typography variant="body2" fontWeight={600}>Cost Reduction</Typography>
                </Box>
                <Box>
                  <Typography variant="h5" color="info.dark" fontWeight={700}>25%</Typography>
                  <Typography variant="body2" fontWeight={600}>Performance Gain</Typography>
                </Box>
                <Box>
                  <Typography variant="h5" color="warning.dark" fontWeight={700}>{formatCurrency(annual_savings)}</Typography>
                  <Typography variant="body2" fontWeight={600}>Annual Savings</Typography>
                </Box>
                <Box>
                  <Typography variant="h5" color="secondary.dark" fontWeight={700}>15%</Typography>
                  <Typography variant="body2" fontWeight={600}>Extra Optimization</Typography>
                </Box>
              </Box>
            </Card>

            {/* Migration Benefits */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Key Migration Benefits:</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
              <Box sx={{ p: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>OCI VM.Standard.E4.Flex Shapes</Typography>
                <Typography variant="body2">Right-sized compute for Lightsail workloads • 25% better price/performance • AMD EPYC processors</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>Flexible Shapes Technology</Typography>
                <Typography variant="body2">Customize CPU/memory independently • Scale from 1-4 OCPUs for small workloads • Pay for exactly what you need</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                <Typography variant="subtitle2" fontWeight={600}>Always-Free Tier Eligible</Typography>
                <Typography variant="body2">Up to 4 OCPUs and 24GB RAM free forever • Ideal for development workloads • No time limits</Typography>
              </Box>
            </Box>
          </Box>
        )
      },
      {
        title: "Storage Migration Overview",
        subtitle: `${oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('s3') || m.aws_service.toLowerCase().includes('ebs')).length || 'Multiple'} storage services → OCI Object/Block Storage`,
        content: (
          <Box>
            {/* Hero Impact Numbers */}
            <Box sx={{ textAlign: 'center', mb: 3, p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '2px solid', borderColor: 'primary.200' }}>
              <Typography variant="h2" sx={{ color: 'primary.dark', fontWeight: 900, mb: 1 }}>
                {monthly_savings ? '$' + (monthly_savings * 0.3 / 1000).toFixed(0) + 'K' : '$201K'}
              </Typography>
              <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 600, mb: 1, textTransform: 'uppercase' }}>
                Monthly Storage Savings
              </Typography>
              <Typography variant="body1" sx={{ color: 'grey.700' }}>
                50% cost reduction with 100% higher IOPS performance
              </Typography>
            </Box>

            {/* Key Storage Stats */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="warning.dark">{oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('s3') || m.aws_service.toLowerCase().includes('ebs')).length || 0}</Typography>
                <Typography variant="body2" fontWeight={600}>Storage Services</Typography>
                <Typography variant="caption">S3 + EBS workloads</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="success.dark">{cost_comparison?.oci_monthly_cost ? '$' + (cost_comparison.oci_monthly_cost * 0.3 / 1000).toFixed(0) + 'K' : '$190K'}</Typography>
                <Typography variant="body2" fontWeight={600}>Target OCI Cost</Typography>
                <Typography variant="caption">vs {cost_comparison?.aws_monthly_cost ? '$' + (cost_comparison.aws_monthly_cost * 0.3 / 1000).toFixed(0) + 'K' : '$391K'} AWS</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="info.dark">480K</Typography>
                <Typography variant="body2" fontWeight={600}>IOPS Performance</Typography>
                <Typography variant="caption">vs 240K AWS</Typography>
              </Box>
            </Box>

            {/* Storage Comparison */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ p: 2, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                <Typography variant="h6" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>
                  Current AWS Storage
                </Typography>
                <Typography variant="body2">
                  • S3: Standard, IA, Glacier tiers<br/>
                  • EBS: gp3, io2, st1, sc1 volumes<br/>
                  • Complex IOPS pricing<br/>
                  • High egress charges
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                <Typography variant="h6" fontWeight={700} color="success.dark" sx={{ mb: 1 }}>
                  Target OCI Storage
                </Typography>
                <Typography variant="body2">
                  • Object Storage: 43% savings<br/>
                  • Block Volumes: 57% savings<br/>
                  • Higher IOPS included<br/>
                  • Free regional egress
                </Typography>
              </Box>
            </Box>

            {/* Key Benefits */}
            <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                Storage Migration Benefits
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.dark" fontWeight={700}>100%</Typography>
                  <Typography variant="body2" fontWeight={600}>Higher IOPS</Typography>
                  <Typography variant="caption">No extra charges</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="info.dark" fontWeight={700}>FREE</Typography>
                  <Typography variant="body2" fontWeight={600}>Regional Egress</Typography>
                  <Typography variant="caption">Eliminate AWS fees</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.dark" fontWeight={700}>{formatCurrency(annual_savings ? annual_savings * 0.3 : null)}</Typography>
                  <Typography variant="body2" fontWeight={600}>Annual Savings</Typography>
                  <Typography variant="caption">50% cost reduction</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )
      },
      {
        title: "Networking & CDN Migration",
        subtitle: `${oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('cloudfront') || m.aws_service.toLowerCase().includes('data transfer')).length || 'Multiple'} networking services → OCI Edge & Networking`,
        content: (
          <Box>
            {/* Networking Comparison */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ p: 3, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                <Typography variant="h6" fontWeight={700} color="warning.dark" sx={{ mb: 2 }}>
                  Current AWS Networking
                </Typography>
                <Typography variant="subtitle2" fontWeight={600}>Data Transfer:</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  • {oci_mappings.find((m: any) => m.aws_service.toLowerCase().includes('data transfer'))?.aws_cost ? '$' + oci_mappings.find((m: any) => m.aws_service.toLowerCase().includes('data transfer')).aws_cost.toLocaleString() : '$0'}/month<br/>
                  • Complex pricing tiers<br/>
                  • High data transfer costs
                </Typography>
                <Typography variant="subtitle2" fontWeight={600}>VPC & Load Balancing:</Typography>
                <Typography variant="body2">
                  • Application Load Balancer<br/>
                  • NAT Gateway charges<br/>
                  • Data transfer fees<br/>
                  • Route 53 DNS costs
                </Typography>
              </Box>
              <Box sx={{ p: 3, bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                <Typography variant="h6" fontWeight={700} color="success.dark" sx={{ mb: 2 }}>
                  Target OCI Networking
                </Typography>
                <Typography variant="subtitle2" fontWeight={600}>Data Transfer:</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  • {oci_mappings.find((m: any) => m.aws_service.toLowerCase().includes('data transfer'))?.estimated_oci_cost ? '$' + oci_mappings.find((m: any) => m.aws_service.toLowerCase().includes('data transfer')).estimated_oci_cost.toLocaleString() : '$0'}/month<br/>
                  • Global edge network<br/>
                  • Built-in DDoS protection
                </Typography>
                <Typography variant="subtitle2" fontWeight={600}>VCN & Load Balancing:</Typography>
                <Typography variant="body2">
                  • Flexible Load Balancer<br/>
                  • Free NAT Gateway<br/>
                  • Free regional egress<br/>
                  • DNS included
                </Typography>
              </Box>
            </Box>

            {/* Cost Comparison */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="warning.dark">{oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('data transfer')).reduce((sum: number, m: any) => sum + (m.aws_cost || 0), 0) || '$0'}</Typography>
                <Typography variant="body2" fontWeight={600}>Current AWS</Typography>
                <Typography variant="caption">Monthly networking</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="success.dark">{oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('data transfer')).reduce((sum: number, m: any) => sum + (m.estimated_oci_cost || 0), 0) || '$0'}</Typography>
                <Typography variant="body2" fontWeight={600}>Target OCI</Typography>
                <Typography variant="caption">Monthly networking</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                <Typography variant="h4" fontWeight={700} color="primary.dark">35%</Typography>
                <Typography variant="body2" fontWeight={600}>Cost Reduction</Typography>
                <Typography variant="caption">Estimated savings</Typography>
              </Box>
            </Box>

            {/* Key Benefits */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Networking Migration Benefits:</Typography>
            <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2">10TB free data transfer monthly • Built-in DDoS protection • 90% savings after free tier • Simplified pricing model</Typography>
            </Box>
          </Box>
        )
      },
      {
        title: "Database Migration Strategy",
        subtitle: `${oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('rds') || m.aws_service.toLowerCase().includes('dynamodb')).length || 'Multiple'} database services → Autonomous Database & NoSQL`,
        content: (
          <Box>
            {/* Database Cost Comparison */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Box sx={{ p: 3, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                <Typography variant="h6" fontWeight={700} color="warning.dark" sx={{ mb: 2 }}>
                  Current AWS Database
                </Typography>
                <Typography variant="body2">RDS: {oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('rds')).reduce((sum: number, m: any) => sum + (m.aws_cost || 0), 0) ? '$' + oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('rds')).reduce((sum: number, m: any) => sum + (m.aws_cost || 0), 0).toLocaleString() : '$0'}/month</Typography>
                <Typography variant="body2">DynamoDB: {oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('dynamodb')).reduce((sum: number, m: any) => sum + (m.aws_cost || 0), 0) ? '$' + oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('dynamodb')).reduce((sum: number, m: any) => sum + (m.aws_cost || 0), 0).toLocaleString() : '$0'}/month</Typography>
                <Typography variant="body2">Backup & Monitoring included in cost</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                  Total: {oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('rds') || m.aws_service.toLowerCase().includes('dynamodb')).reduce((sum: number, m: any) => sum + (m.aws_cost || 0), 0) ? '$' + oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('rds') || m.aws_service.toLowerCase().includes('dynamodb')).reduce((sum: number, m: any) => sum + (m.aws_cost || 0), 0).toLocaleString() : '$0'}/month
                </Typography>
              </Box>
              <Box sx={{ p: 3, bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                <Typography variant="h6" fontWeight={700} color="success.dark" sx={{ mb: 2 }}>
                  Target OCI Database
                </Typography>
                <Typography variant="body2">Autonomous Database: {oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('rds')).reduce((sum: number, m: any) => sum + (m.estimated_oci_cost || 0), 0) ? '$' + oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('rds')).reduce((sum: number, m: any) => sum + (m.estimated_oci_cost || 0), 0).toLocaleString() : '$0'}/month</Typography>
                <Typography variant="body2">NoSQL Database: {oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('dynamodb')).reduce((sum: number, m: any) => sum + (m.estimated_oci_cost || 0), 0) ? '$' + oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('dynamodb')).reduce((sum: number, m: any) => sum + (m.estimated_oci_cost || 0), 0).toLocaleString() : '$0'}/month</Typography>
                <Typography variant="body2">Backup included: $0/month</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ mt: 1, color: 'success.dark' }}>
                  Total: {oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('rds') || m.aws_service.toLowerCase().includes('dynamodb')).reduce((sum: number, m: any) => sum + (m.estimated_oci_cost || 0), 0) ? '$' + oci_mappings.filter((m: any) => m.aws_service.toLowerCase().includes('rds') || m.aws_service.toLowerCase().includes('dynamodb')).reduce((sum: number, m: any) => sum + (m.estimated_oci_cost || 0), 0).toLocaleString() : '$0'}/month
                </Typography>
              </Box>
            </Box>

            {/* Key Benefits */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Database Migration Benefits:</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
              <Box sx={{ p: 2, bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                <Typography variant="body2">World's first self-managing database • 80% DBA workload reduction • Automated patching and tuning</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
                <Typography variant="body2">Unified SQL+NoSQL platform • Reduce vendor complexity • Built-in machine learning capabilities</Typography>
              </Box>
            </Box>
          </Box>
        )
      },
      {
        title: "Total Financial Impact",
        subtitle: "Complete TCO analysis and business benefits",
        content: (
          <Box>
            {/* Hero Savings */}
            <Box sx={{ textAlign: 'center', mb: 4, p: 4, bgcolor: 'primary.50', borderRadius: 2, border: '2px solid', borderColor: 'primary.200' }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 1, textTransform: 'uppercase' }}>
                5-Year Total Savings
              </Typography>
              <Typography component="div" sx={{ 
                color: 'primary.dark', 
                fontWeight: 900, 
                mb: 2,
                fontSize: '80px !important',
                lineHeight: '70px',
                textShadow: '0 4px 8px rgba(0,0,0,0.2)',
                letterSpacing: '-2px'
              }}>
                {formatCurrency(annual_savings ? annual_savings * 5.5 : null)}
              </Typography>
              <Typography variant="h6" sx={{ color: 'grey.700' }}>
                Including migration services, training, and optimization
              </Typography>
            </Box>

            {/* Monthly & Annual Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'warning.50', minHeight: 200 }}>
                <Typography variant="subtitle1" color="warning.main" fontWeight={600}>Monthly Savings</Typography>
                <Typography variant="h4" sx={{ color: 'warning.dark' }}>
                  {monthly_savings ? '$' + (monthly_savings / 1000).toFixed(0) + 'K' : 'N/A'}
                </Typography>
                <Typography variant="caption" color="warning.dark">Per month</Typography>
              </Card>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'success.50', minHeight: 200 }}>
                <Typography variant="subtitle1" color="success.main" fontWeight={600}>Annual Savings</Typography>
                <Typography variant="h4" sx={{ color: 'success.dark' }}>
                  {formatCurrency(annual_savings)}
                </Typography>
                <Typography variant="caption" color="success.dark">Per year</Typography>
              </Card>
            </Box>

            {/* Next Steps */}
            <Box sx={{ p: 3, bgcolor: 'info.50', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Recommended Next Steps:</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Oracle Cloud Credits evaluation ({analysisResult.bill_name ? 'for ' + analysisResult.bill_name : '$50K available'})
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Detailed migration assessment (2-week engagement)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Proof of Concept for critical workloads
              </Typography>
              <Typography variant="body2">
                • Oracle FastConnect setup for hybrid connectivity
              </Typography>
            </Box>
          </Box>
        )
      }
    ];

    const totalSlides = slides.length;

    const nextSlide = () => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    };

    const prevSlide = () => {
      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    };

    return (
      <Box sx={{ 
        border: '2px solid', 
        borderColor: 'grey.300', 
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        bgcolor: 'white',
        overflow: 'visible'
      }}>
        {/* Header with title and controls */}
        <Box sx={{ 
          p: 1, 
          borderBottom: '1px solid', 
          borderColor: 'grey.200',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          bgcolor: 'grey.50'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SlideshowIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Sales Deck Preview
            </Typography>
          </Box>
          
          {/* Auto-generated sales deck info */}
          <Stack direction="row" spacing={1}>
            <Chip 
              label={salesDeck ? 'AI Deck Generated' : 'Generating AI Deck...'} 
              color={salesDeck ? 'success' : 'default'}
              size="small"
              icon={salesDeck ? <CheckIcon /> : <CircularProgress size={16} />}
            />
          </Stack>
        </Box>

        {/* Slide Navigation */}
        <Box sx={{ 
          p: 0.5, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid', 
          borderColor: 'grey.200',
          bgcolor: 'grey.25'
        }}>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={prevSlide}
            disabled={currentSlide === 0}
            sx={{ minWidth: 80 }}
          >
            ← Previous
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Slide {currentSlide + 1} of {totalSlides}
            </Typography>
            {/* Slide indicators */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {Array.from({ length: totalSlides }).map((_, index) => (
                <Box 
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: index === currentSlide ? 'primary.main' : 'grey.300',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }} 
                />
              ))}
            </Box>
            {/* See Final Deck Button */}
            {salesDeck && (
              <Button
                variant="contained"
                size="small"
                onClick={() => setShowSalesDeck(true)}
                sx={{ 
                  ml: 2,
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                See Final Deck
              </Button>
            )}
          </Box>
          
          <Button 
            variant="outlined" 
            size="small" 
            onClick={nextSlide}
            disabled={currentSlide === totalSlides - 1}
            sx={{ minWidth: 80 }}
          >
            Next →
          </Button>
        </Box>

        {/* Current Slide Content */}
        <Box sx={{ p: 4, minHeight: '600px', display: 'flex', flexDirection: 'column' }}>
          {deckError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deckError}
            </Alert>
          )}
          
          {slides[currentSlide] && (
            <Box sx={{ flex: 1, overflow: 'visible' }}>
              <Typography variant="h5" sx={{ color: 'grey.900', fontWeight: 700, mb: 1, fontSize: '1.25rem' }}>
                {slides[currentSlide].title}
              </Typography>
              <Typography variant="body1" sx={{ color: 'grey.600', mb: 2, fontSize: '1rem' }}>
                {slides[currentSlide].subtitle}
              </Typography>
              <Box sx={{ typography: 'body1', '& .MuiTypography-root': { fontSize: '1rem', lineHeight: 1.6 } }}>
                {slides[currentSlide].content}
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ 
          p: 0.5, 
          bgcolor: 'grey.50', 
          borderTop: '1px solid', 
          borderColor: 'grey.200',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Typography variant="caption" color="text.secondary">
            Generated with Alfred Cloud Compare • Oracle Cloud Infrastructure Migration Analysis
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderNetworkingAnalysis = () => {
    // Extract networking-specific services from customer's bill
    const networkingServices = oci_mappings.filter((mapping: any) => 
      mapping.aws_service.toLowerCase().includes('cloudfront') ||
      mapping.aws_service.toLowerCase().includes('vpc') ||
      mapping.aws_service.toLowerCase().includes('load balancer') ||
      mapping.aws_service.toLowerCase().includes('load balancing') ||
      mapping.aws_service.toLowerCase().includes('elb') ||
      mapping.aws_service.toLowerCase().includes('alb') ||
      mapping.aws_service.toLowerCase().includes('nlb') ||
      mapping.aws_service.toLowerCase().includes('route 53') ||
      mapping.aws_service.toLowerCase().includes('direct connect') ||
      mapping.aws_service.toLowerCase().includes('vpn') ||
      mapping.aws_service.toLowerCase().includes('transit gateway') ||
      mapping.aws_service.toLowerCase().includes('nat gateway') ||
      mapping.aws_service.toLowerCase().includes('internet gateway') ||
      mapping.aws_service.toLowerCase().includes('api gateway') ||
      mapping.aws_service.toLowerCase().includes('cloudwatch') ||
      mapping.aws_service.toLowerCase().includes('waf') ||
      mapping.aws_service.toLowerCase().includes('data transfer')
    );

    const networkingCategories = networkingServices.reduce((acc: any, service: any) => {
      const category = service.aws_service.toLowerCase().includes('cloudfront') ? 'CDN & Edge' :
                     service.aws_service.toLowerCase().includes('load balancer') || 
                     service.aws_service.toLowerCase().includes('elb') ||
                     service.aws_service.toLowerCase().includes('alb') ||
                     service.aws_service.toLowerCase().includes('nlb') ? 'Load Balancing' :
                     service.aws_service.toLowerCase().includes('route 53') ? 'DNS' :
                     service.aws_service.toLowerCase().includes('direct connect') ||
                     service.aws_service.toLowerCase().includes('vpn') ? 'Connectivity' :
                     service.aws_service.toLowerCase().includes('vpc') ||
                     service.aws_service.toLowerCase().includes('nat gateway') ||
                     service.aws_service.toLowerCase().includes('internet gateway') ||
                     service.aws_service.toLowerCase().includes('transit gateway') ? 'VPC & Gateways' :
                     service.aws_service.toLowerCase().includes('api gateway') ? 'API Management' :
                     service.aws_service.toLowerCase().includes('waf') ? 'Security' :
                     service.aws_service.toLowerCase().includes('data transfer') ? 'Data Transfer' :
                     'Other Networking';
      
      if (!acc[category]) {
        acc[category] = { count: 0, cost: 0, oci_cost: 0 };
      }
      acc[category].count += 1;
      acc[category].cost += service.aws_cost || 0;
      acc[category].oci_cost += service.estimated_oci_cost || 0;
      return acc;
    }, {});

    const totalNetworkingCost = networkingServices.reduce((sum: number, service: any) => sum + (service.aws_cost || 0), 0);
    const totalOCINetworkingCost = networkingServices.reduce((sum: number, service: any) => sum + (service.estimated_oci_cost || 0), 0);
    const networkingSavings = totalNetworkingCost - totalOCINetworkingCost;

    const categoryData = Object.entries(networkingCategories).map(([name, data]: [string, any]) => ({
      name,
      services: data.count,
      aws_cost: data.cost,
      oci_cost: data.oci_cost,
      savings: data.cost - data.oci_cost
    }));

    return (
      <Box>
        {/* Networking Overview Cards */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'info.50' }}>
            <NetworkCheckIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Network Services</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main' }}>
              {networkingServices.length}
            </Typography>
            <Typography variant="body2" color="info.700">Active networking services</Typography>
          </Card>
          
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'warning.50' }}>
            <PublicIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>AWS Network Cost</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
              ${totalNetworkingCost.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="warning.700">Monthly networking spend</Typography>
          </Card>
          
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'success.50' }}>
            <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Network Savings</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
              ${networkingSavings.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="success.700">Monthly network savings</Typography>
          </Card>
        </Stack>

        {/* Networking Services Breakdown */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'info.main', fontWeight: 600, mb: 3 }}>
              Your Networking Services Analysis
            </Typography>
            
            {categoryData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Service Category</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Services</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>AWS Cost</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>OCI Cost</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Monthly Savings</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Key OCI Advantage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryData.map((category, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight={600}>{category.name}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={category.services} color="primary" size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="warning.main">
                            ${category.aws_cost.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            ${category.oci_cost.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="success.dark">
                            ${category.savings.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption" color="info.main">
                            {category.name === 'CDN & Edge' ? 'OCI CDN + 10TB free egress' :
                             category.name === 'Load Balancing' ? 'Integrated security included' :
                             category.name === 'DNS' ? 'Higher SLA + lower cost' :
                             category.name === 'Connectivity' ? 'FastConnect no data fees' :
                             category.name === 'VPC & Gateways' ? 'Free VCN & all gateways' :
                             category.name === 'API Management' ? 'Built-in caching + security' :
                             category.name === 'Security' ? 'WAF/DDoS included free' :
                             '90% lower egress costs'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No networking services detected in your AWS bill. This analysis focuses on services like CloudFront, ELB, VPC, Route 53, etc.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Egress Cost Savings - Major Differentiator */}
        <Card sx={{ mb: 4, bgcolor: 'success.50', border: '3px solid', borderColor: 'success.300' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'success.dark', fontWeight: 700, mb: 3 }}>
              💰 Massive Egress Cost Savings - OCI's Biggest Advantage
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ color: 'error.main', fontWeight: 700, mb: 2 }}>
                  AWS Egress Charges (Per GB)
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>• Regional transfer: <strong>$0.02/GB</strong></Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>• Internet egress: <strong>$0.09/GB</strong> (first 100GB free)</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>• CloudFront: <strong>$0.085/GB</strong></Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>• Data Transfer Out: <strong>$0.09-$0.05/GB</strong></Typography>
                
                <Typography variant="subtitle1" sx={{ color: 'error.main', fontWeight: 700, mb: 1 }}>
                  Estimated Monthly Egress Cost
                </Typography>
                <Typography variant="h5" sx={{ color: 'error.main', fontWeight: 700 }}>
                  ${Math.round(totalNetworkingCost * 0.3).toLocaleString()} - ${Math.round(totalNetworkingCost * 0.5).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="error.600">
                  (30-50% of networking costs are typically egress)
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ color: 'success.main', fontWeight: 700, mb: 2 }}>
                  OCI Egress Pricing
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>• First <strong>10 TB/month: FREE</strong> 🎉</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>• Over 10TB: <strong>$0.0085/GB</strong> (90% less than AWS)</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>• Regional transfer: <strong>FREE</strong></Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>• FastConnect: <strong>No data charges</strong></Typography>
                
                <Typography variant="subtitle1" sx={{ color: 'success.main', fontWeight: 700, mb: 1 }}>
                  Your Estimated Monthly Savings
                </Typography>
                <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700 }}>
                  ${Math.round(totalNetworkingCost * 0.4).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="success.600">
                  (40%+ savings on networking costs)
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* CDN and Edge Services */}
        <Card sx={{ mb: 4, bgcolor: 'info.50', border: '2px solid', borderColor: 'info.200' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'info.main', fontWeight: 600, mb: 3 }}>
              🌐 CDN & Edge Services Strategy
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
                  OCI Native CDN Platform
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  • <strong>150 countries coverage</strong> (launched May 2025)<br/>
                  • Competitive alternative to CloudFront<br/>
                  • Integrated with OCI services<br/>
                  • Lower egress costs than AWS CloudFront
                </Typography>
                
                <Typography variant="subtitle1" sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
                  Partner CDN Integration
                </Typography>
                <Typography variant="body2">
                  • <strong>Akamai integration</strong> available<br/>
                  • Cloudflare partnership options<br/>
                  • Flexible multi-CDN strategies<br/>
                  • Use existing CDN contracts
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ color: 'warning.main', fontWeight: 700, mb: 1 }}>
                  Migration Strategy
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>CloudFront → OCI CDN:</strong> Direct replacement with 90% cost reduction<br/>
                  <strong>CloudFront → Keep Akamai:</strong> Move origin to OCI, keep existing CDN<br/>
                  <strong>Hybrid approach:</strong> OCI CDN for new content, existing CDN for legacy
                </Typography>
                
                <Typography variant="subtitle1" sx={{ color: 'success.main', fontWeight: 700, mb: 1 }}>
                  Cost Impact
                </Typography>
                <Typography variant="body2">
                  • Eliminate CloudFront charges<br/>
                  • Reduce origin egress by 90%+<br/>
                  • Free 10TB monthly transfer<br/>
                  • Lower CDN per-GB costs
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Other Networking Advantages */}
        <Card sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'grey.800', fontWeight: 600, mb: 3 }}>
              Additional OCI Networking Advantages
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ color: 'info.main', fontWeight: 700, mb: 1 }}>
                  ⚡ Performance
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  • <strong>50 Gbps networking</strong> (vs AWS 25 Gbps)<br/>
                  • True bare metal performance<br/>
                  • Lower latency FastConnect<br/>
                  • Non-blocking network fabric
                </Typography>
                
                <Typography variant="subtitle1" sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
                  🛡️ Security
                </Typography>
                <Typography variant="body2">
                  • Network Security Groups included<br/>
                  • Native DDoS protection<br/>
                  • Web Application Firewall<br/>
                  • Private endpoints by default
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ color: 'warning.main', fontWeight: 700, mb: 1 }}>
                  📡 Connectivity
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  • <strong>FastConnect</strong> with no data charges<br/>
                  • Multiple provider partnerships<br/>
                  • Dedicated connectivity options<br/>
                  • Site-to-site VPN included
                </Typography>
                
                <Typography variant="subtitle1" sx={{ color: 'success.main', fontWeight: 700, mb: 1 }}>
                  💡 Simplicity
                </Typography>
                <Typography variant="body2">
                  • Free VCN and subnets<br/>
                  • No NAT gateway charges<br/>
                  • Simplified load balancer pricing<br/>
                  • Predictable monthly costs
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderStorageAnalysis = () => {
    const storageServices = oci_mappings.filter((mapping: any) => 
      mapping.aws_service.includes('S3') || mapping.aws_service.includes('EBS') || 
      mapping.aws_service.includes('Block Store') || mapping.aws_service.includes('Storage') || 
      mapping.aws_service.includes('EFS')
    );


    const storageComparisonData = [
      { type: 'Object Storage', AWS: 0.023, OCI: 0.0255, performance: '99.9%' },
      { type: 'Block Storage - Standard', AWS: 0.10, OCI: 0.0425, performance: '99.95%' },
      { type: 'Block Storage - High Perf', AWS: 0.125, OCI: 0.085, performance: '99.99%' },
      { type: 'File Storage', AWS: 0.30, OCI: 0.20, performance: '99.9%' }
    ];

    return (
      <Box>
        {/* Storage Overview Cards */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'info.50' }}>
            <StorageIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Storage Savings</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main' }}>
              ${storageServices.reduce((sum: number, service: any) => sum + (service.aws_cost - service.estimated_oci_cost), 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="info.700">Monthly storage savings</Typography>
          </Card>
          
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'secondary.50' }}>
            <SpeedIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>IOPS Improvement</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main' }}>
              +180%
            </Typography>
            <Typography variant="body2" color="secondary.700">Higher performance storage</Typography>
          </Card>
          
          <Card sx={{ flex: 1, p: 3, textAlign: 'center', bgcolor: 'success.50' }}>
            <DataUsageIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Throughput Gain</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
              +250%
            </Typography>
            <Typography variant="body2" color="success.700">Enhanced data transfer</Typography>
          </Card>
        </Stack>

        {/* Storage Type Analysis */}
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ mb: 4 }}>
          <Card elevation={2} sx={{ flex: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Storage Cost Comparison (per GB/month)</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={storageComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value}`} />
                  <Legend />
                  <Bar dataKey="AWS" fill="#FF8042" />
                  <Bar dataKey="OCI" fill="#00C49F" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Storage Performance</Typography>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>Object Storage</Typography>
                  <Typography variant="body2" color="text.secondary">
                    11 9's durability, free egress within region
                  </Typography>
                  <Chip label="40% cheaper" color="success" size="small" sx={{ mt: 1 }} />
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>Block Storage</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Up to 225K IOPS, 2.4 GB/s throughput
                  </Typography>
                  <Chip label="60% cheaper" color="success" size="small" sx={{ mt: 1 }} />
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>File Storage</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fully managed NFS, automatic scaling
                  </Typography>
                  <Chip label="35% cheaper" color="success" size="small" sx={{ mt: 1 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* Storage Details Table */}
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Detailed Storage Analysis</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Storage Type</strong></TableCell>
                    <TableCell><strong>AWS Service</strong></TableCell>
                    <TableCell><strong>OCI Service</strong></TableCell>
                    <TableCell align="right"><strong>AWS Cost</strong></TableCell>
                    <TableCell align="right"><strong>OCI Cost</strong></TableCell>
                    <TableCell align="center"><strong>Price Difference</strong></TableCell>
                    <TableCell align="center"><strong>Performance</strong></TableCell>
                    <TableCell align="center"><strong>Features</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storageServices.map((service: any, index: number) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Chip 
                          label={service.aws_service.includes('S3') ? 'Object' : 
                                 (service.aws_service.includes('EBS') || service.aws_service.includes('Block Store')) ? 'Block' : 'File'} 
                          color="primary" size="small" 
                        />
                      </TableCell>
                      <TableCell>{service.aws_service}</TableCell>
                      <TableCell>{service.oci_service} - {service.oci_product}</TableCell>
                      <TableCell align="right">${service.aws_cost?.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        ${service.estimated_oci_cost?.toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const difference = service.aws_cost - service.estimated_oci_cost;
                          const isPositive = difference > 0;
                          const percentage = ((Math.abs(difference) / service.aws_cost) * 100).toFixed(0);
                          return (
                            <Chip 
                              label={`${isPositive ? '-' : '+'}$${Math.abs(difference).toLocaleString()} (${percentage}%)`}
                              color={isPositive ? "success" : "error"} 
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const storageType = service.aws_service.includes('S3') ? 'Object' : 
                                            (service.aws_service.includes('EBS') || service.aws_service.includes('Block Store')) ? 'Block' : 'File';
                          
                          // Specific performance metrics based on storage type and service
                          if (storageType === 'Block') {
                            if (service.aws_service.includes('Block Store')) {
                              // EBS performance varies by type
                              if (service.oci_product?.includes('Ultra High Performance')) {
                                return <Chip label="4.7x max IOPS" color="success" size="small" />;
                              } else if (service.oci_product?.includes('Balanced')) {
                                return <Chip label="20x baseline IOPS" color="success" size="small" />;
                              } else {
                                return <Chip label="+180% IOPS" color="success" size="small" />;
                              }
                            }
                            return <Chip label="+180% IOPS" color="success" size="small" />;
                          } else if (storageType === 'Object') {
                            if (service.aws_service.includes('Glacier') || service.aws_service.includes('Archive')) {
                              return <Chip label="Instant retrieval" color="success" size="small" />;
                            } else {
                              return <Chip label="Comparable" color="default" size="small" />;
                            }
                          } else if (storageType === 'File') {
                            if (service.aws_service.includes('FSx') && service.aws_service.includes('Lustre')) {
                              return <Chip label="+40% throughput" color="success" size="small" />;
                            } else if (service.aws_service.includes('EFS')) {
                              return <Chip label="Linear scaling" color="success" size="small" />;
                            } else {
                              return <Chip label="+35% faster" color="success" size="small" />;
                            }
                          }
                          return <Chip label="Comparable" color="default" size="small" />;
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label="Enhanced" color="info" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderResearchSection = () => {
    if (!researchData || !performanceData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SearchIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          Enterprise AWS vs OCI Research & Analysis
        </Typography>

        {/* Research Overview */}
        <Card elevation={2} sx={{ mb: 4, p: 3, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.200' }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
            🏢 Enterprise-Grade Research Methodology
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Comprehensive analysis of enterprise workloads including bare metal instances, GPU computing, 
            CDN services, and high-performance networking with specific benchmark data and TCO calculations.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip label="SPEC CPU 2017 Benchmarks" color="primary" />
            <Chip label="Real Enterprise Pricing" color="secondary" />
            <Chip label="Bare Metal vs Virtualized" color="success" />
            <Chip label="3-Year TCO Analysis" color="warning" />
          </Stack>
        </Card>

        {/* Research Tabs */}
        <Card elevation={2} sx={{ mb: 4 }}>
          <Tabs 
            value={activeResearchTab} 
            onChange={(e, newValue) => setActiveResearchTab(newValue)}
            sx={{ 
              '& .MuiTabs-indicator': { height: 3 },
              '& .MuiTab-root': { 
                minHeight: 48,
                fontWeight: 500,
                fontSize: '0.9rem'
              }
            }}
          >
            <Tab label="Enterprise Compute" value="enterprise-compute" />
            <Tab label="Bare Metal vs Virtual" value="bare-metal" />
            <Tab label="GPU & ML Performance" value="gpu-ml" />
            <Tab label="CDN & Edge Services" value="cdn-edge" />
            <Tab label="Performance Benchmarks" value="benchmarks" />
            <Tab label="TCO Analysis" value="tco" />
          </Tabs>
        </Card>

        {/* Enterprise Compute Comparison */}
        {activeResearchTab === 'enterprise-compute' && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
              Enterprise Compute Family Comparison
            </Typography>
            
            {/* General Purpose Enterprise */}
            <Card elevation={2} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <ComputerIcon sx={{ mr: 1, color: 'primary.main' }} />
                  General Purpose Enterprise Instances
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Provider</strong></TableCell>
                        <TableCell><strong>SKU</strong></TableCell>
                        <TableCell align="center"><strong>CPU/OCPU</strong></TableCell>
                        <TableCell align="center"><strong>Memory (GB)</strong></TableCell>
                        <TableCell align="right"><strong>$/Month</strong></TableCell>
                        <TableCell align="center"><strong>Network</strong></TableCell>
                        <TableCell align="center"><strong>Cost Advantage</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {researchData.enterprise_compute_families.general_purpose.aws_m_series.map((instance: any, index: number) => (
                        <TableRow key={`aws-${index}`} hover sx={{ bgcolor: 'warning.50' }}>
                          <TableCell><Chip label="AWS" color="warning" size="small" /></TableCell>
                          <TableCell><strong>{instance.sku}</strong></TableCell>
                          <TableCell align="center">{instance.vcpu} vCPU</TableCell>
                          <TableCell align="center">{instance.memory_gb}</TableCell>
                          <TableCell align="right">${instance.price_per_month.toLocaleString()}</TableCell>
                          <TableCell align="center">{instance.network_gbps} Gbps</TableCell>
                          <TableCell align="center">-</TableCell>
                        </TableRow>
                      ))}
                      {researchData.enterprise_compute_families.general_purpose.oci_standard_series.map((instance: any, index: number) => (
                        <TableRow key={`oci-${index}`} hover sx={{ bgcolor: 'success.50' }}>
                          <TableCell><Chip label="OCI" color="success" size="small" /></TableCell>
                          <TableCell><strong>{instance.sku}</strong></TableCell>
                          <TableCell align="center">{instance.ocpu} OCPU</TableCell>
                          <TableCell align="center">{instance.memory_gb}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                            ${instance.price_per_month.toLocaleString()}
                          </TableCell>
                          <TableCell align="center">{instance.network_gbps} Gbps</TableCell>
                          <TableCell align="center">
                            <Chip label={instance.cost_advantage} color="success" size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* GPU Instances */}
            <Card elevation={2} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <DataUsageIcon sx={{ mr: 1, color: 'primary.main' }} />
                  GPU & ML Instances
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Provider</strong></TableCell>
                        <TableCell><strong>SKU</strong></TableCell>
                        <TableCell align="center"><strong>GPUs</strong></TableCell>
                        <TableCell align="center"><strong>GPU Type</strong></TableCell>
                        <TableCell align="right"><strong>$/Month</strong></TableCell>
                        <TableCell align="center"><strong>$/GPU/Hour</strong></TableCell>
                        <TableCell align="center"><strong>Advantage</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {researchData.enterprise_compute_families.gpu_instances.aws_gpu_series.map((instance: any, index: number) => (
                        <TableRow key={`aws-gpu-${index}`} hover sx={{ bgcolor: 'warning.50' }}>
                          <TableCell><Chip label="AWS" color="warning" size="small" /></TableCell>
                          <TableCell><strong>{instance.sku}</strong></TableCell>
                          <TableCell align="center">{instance.gpus}x {instance.gpu_type}</TableCell>
                          <TableCell align="center">{instance.gpu_type}</TableCell>
                          <TableCell align="right">${instance.price_per_month.toLocaleString()}</TableCell>
                          <TableCell align="center">${instance.price_per_gpu_hour.toFixed(3)}</TableCell>
                          <TableCell align="center">-</TableCell>
                        </TableRow>
                      ))}
                      {researchData.enterprise_compute_families.gpu_instances.oci_gpu_series.map((instance: any, index: number) => (
                        <TableRow key={`oci-gpu-${index}`} hover sx={{ bgcolor: 'success.50' }}>
                          <TableCell><Chip label="OCI" color="success" size="small" /></TableCell>
                          <TableCell><strong>{instance.sku}</strong></TableCell>
                          <TableCell align="center">{instance.gpus}x {instance.gpu_type}</TableCell>
                          <TableCell align="center">{instance.gpu_type}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                            ${instance.price_per_month.toLocaleString()}
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                            ${instance.price_per_gpu_hour.toFixed(3)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={instance.cost_advantage} color="success" size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Performance Benchmarks Tab */}
        {activeResearchTab === 'benchmarks' && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
              Enterprise Performance Benchmarks
            </Typography>
            
            {/* SPEC CPU Benchmarks */}
            <Card elevation={2} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  SPEC CPU 2017 Benchmark Results - Bare Metal Comparison
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Metric</strong></TableCell>
                        <TableCell align="center"><strong>AWS m6i.metal</strong></TableCell>
                        <TableCell align="center"><strong>OCI BM.Standard.E4</strong></TableCell>
                        <TableCell align="center"><strong>OCI Advantage</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>SPEC INT Rate</TableCell>
                        <TableCell align="center">{performanceData.enterprise_performance_benchmarks.bare_metal_compute.aws_m6i_metal.spec_int_rate}</TableCell>
                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          {performanceData.enterprise_performance_benchmarks.bare_metal_compute.oci_bm_standard_e4.spec_int_rate}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="+10.5%" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>SPEC FP Rate</TableCell>
                        <TableCell align="center">{performanceData.enterprise_performance_benchmarks.bare_metal_compute.aws_m6i_metal.spec_fp_rate}</TableCell>
                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          {performanceData.enterprise_performance_benchmarks.bare_metal_compute.oci_bm_standard_e4.spec_fp_rate}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="+13.5%" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Memory Bandwidth (GB/s)</TableCell>
                        <TableCell align="center">{performanceData.enterprise_performance_benchmarks.bare_metal_compute.aws_m6i_metal.memory_bandwidth_gbps}</TableCell>
                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          {performanceData.enterprise_performance_benchmarks.bare_metal_compute.oci_bm_standard_e4.memory_bandwidth_gbps}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="+33.6%" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Virtualization Overhead</TableCell>
                        <TableCell align="center" sx={{ color: 'warning.main' }}>
                          {performanceData.enterprise_performance_benchmarks.bare_metal_compute.aws_m6i_metal.virtualization_overhead}
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          {performanceData.enterprise_performance_benchmarks.bare_metal_compute.oci_bm_standard_e4.virtualization_overhead}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="No overhead" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Storage Performance */}
            <Card elevation={2} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Enterprise Storage Performance Comparison
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Metric</strong></TableCell>
                        <TableCell align="center"><strong>AWS EBS gp3 Max</strong></TableCell>
                        <TableCell align="center"><strong>OCI Ultra High Perf</strong></TableCell>
                        <TableCell align="center"><strong>OCI Advantage</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Maximum IOPS</TableCell>
                        <TableCell align="center">{performanceData.enterprise_performance_benchmarks.storage_performance_detailed.block_storage_enterprise.aws_gp3_max.iops.toLocaleString()}</TableCell>
                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          {performanceData.enterprise_performance_benchmarks.storage_performance_detailed.block_storage_enterprise.oci_ultra_high_performance.iops.toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="+1306%" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Throughput (MB/s)</TableCell>
                        <TableCell align="center">{performanceData.enterprise_performance_benchmarks.storage_performance_detailed.block_storage_enterprise.aws_gp3_max.throughput_mbps.toLocaleString()}</TableCell>
                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          {performanceData.enterprise_performance_benchmarks.storage_performance_detailed.block_storage_enterprise.oci_ultra_high_performance.throughput_mbps.toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="+140%" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Latency (ms)</TableCell>
                        <TableCell align="center">{performanceData.enterprise_performance_benchmarks.storage_performance_detailed.block_storage_enterprise.aws_gp3_max.latency_ms}</TableCell>
                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          {performanceData.enterprise_performance_benchmarks.storage_performance_detailed.block_storage_enterprise.oci_ultra_high_performance.latency_ms}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="-40%" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Bare Metal vs Virtual Tab */}
        {activeResearchTab === 'bare-metal' && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
              Bare Metal vs Virtualized Performance Analysis
            </Typography>
            
            {/* Bare Metal Performance Comparison */}
            <Card elevation={2} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <MemoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                  Enterprise Bare Metal Instances
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Provider</strong></TableCell>
                        <TableCell><strong>Instance Type</strong></TableCell>
                        <TableCell align="center"><strong>Processor</strong></TableCell>
                        <TableCell align="center"><strong>CPU/OCPU</strong></TableCell>
                        <TableCell align="center"><strong>Memory (GB)</strong></TableCell>
                        <TableCell align="center"><strong>Network (Gbps)</strong></TableCell>
                        <TableCell align="right"><strong>$/Month</strong></TableCell>
                        <TableCell align="center"><strong>Performance</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {researchData.enterprise_compute_families.bare_metal_instances?.aws_bare_metal?.map((instance: any, index: number) => (
                        <TableRow key={`aws-bm-${index}`} hover sx={{ bgcolor: 'warning.50' }}>
                          <TableCell><Chip label="AWS" color="warning" size="small" /></TableCell>
                          <TableCell><strong>{instance.sku}</strong></TableCell>
                          <TableCell align="center">{instance.processor}</TableCell>
                          <TableCell align="center">{instance.vcpu} vCPU</TableCell>
                          <TableCell align="center">{instance.memory_gb}</TableCell>
                          <TableCell align="center">{instance.network_gbps}</TableCell>
                          <TableCell align="right">${instance.price_per_month.toLocaleString()}</TableCell>
                          <TableCell align="center">
                            <Chip label="Virtualization Overhead" color="warning" size="small" />
                          </TableCell>
                        </TableRow>
                      )) || []}
                      {researchData.enterprise_compute_families.bare_metal_instances?.oci_bare_metal?.map((instance: any, index: number) => (
                        <TableRow key={`oci-bm-${index}`} hover sx={{ bgcolor: 'success.50' }}>
                          <TableCell><Chip label="OCI" color="success" size="small" /></TableCell>
                          <TableCell><strong>{instance.sku}</strong></TableCell>
                          <TableCell align="center">{instance.processor}</TableCell>
                          <TableCell align="center">{instance.ocpu} OCPU</TableCell>
                          <TableCell align="center">{instance.memory_gb}</TableCell>
                          <TableCell align="center">{instance.network_gbps}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                            ${instance.price_per_month.toLocaleString()}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label="True Bare Metal" color="success" size="small" />
                          </TableCell>
                        </TableRow>
                      )) || []}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Performance Benefits Alert */}
            <Stack spacing={2}>
              <Alert severity="success">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  🏗️ <strong>True Bare Metal:</strong> OCI provides actual bare metal instances with no hypervisor overhead, 
                  delivering up to 15% better performance for CPU-intensive workloads.
                </Typography>
              </Alert>
              <Alert severity="info">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  🔧 <strong>Included Network:</strong> OCI bare metal includes up to 50 Gbps networking at no additional cost, 
                  while AWS charges extra for enhanced networking features.
                </Typography>
              </Alert>
              <Alert severity="warning">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  ⚡ <strong>Memory Flexibility:</strong> OCI allows custom memory configurations even on bare metal, 
                  optimizing cost and performance for specific workloads.
                </Typography>
              </Alert>
            </Stack>
          </Box>
        )}

        {/* GPU & ML Performance Tab */}
        {activeResearchTab === 'gpu-ml' && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
              GPU & ML Performance Analysis
            </Typography>
            
            {/* GPU Performance Comparison */}
            <Card elevation={2} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <DataUsageIcon sx={{ mr: 1, color: 'primary.main' }} />
                  Enterprise GPU Instances for AI/ML
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Provider</strong></TableCell>
                        <TableCell><strong>Instance Type</strong></TableCell>
                        <TableCell align="center"><strong>GPU Count</strong></TableCell>
                        <TableCell align="center"><strong>GPU Memory</strong></TableCell>
                        <TableCell align="center"><strong>Tensor Performance</strong></TableCell>
                        <TableCell align="center"><strong>Interconnect</strong></TableCell>
                        <TableCell align="right"><strong>$/GPU/Hour</strong></TableCell>
                        <TableCell align="center"><strong>ML Advantage</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {researchData.enterprise_compute_families.gpu_instances?.aws_gpu_series?.map((instance: any, index: number) => (
                        <TableRow key={`aws-gpu-detail-${index}`} hover sx={{ bgcolor: 'warning.50' }}>
                          <TableCell><Chip label="AWS" color="warning" size="small" /></TableCell>
                          <TableCell><strong>{instance.sku}</strong></TableCell>
                          <TableCell align="center">{instance.gpus}x {instance.gpu_type}</TableCell>
                          <TableCell align="center">{instance.gpu_memory_gb || '80'} GB</TableCell>
                          <TableCell align="center">{instance.tensor_performance || '312'} TFLOPS</TableCell>
                          <TableCell align="center">{instance.interconnect || 'NVLink'}</TableCell>
                          <TableCell align="right">${instance.price_per_gpu_hour.toFixed(3)}</TableCell>
                          <TableCell align="center">
                            <Chip label="Standard" color="default" size="small" />
                          </TableCell>
                        </TableRow>
                      )) || []}
                      {researchData.enterprise_compute_families.gpu_instances?.oci_gpu_series?.map((instance: any, index: number) => (
                        <TableRow key={`oci-gpu-detail-${index}`} hover sx={{ bgcolor: 'success.50' }}>
                          <TableCell><Chip label="OCI" color="success" size="small" /></TableCell>
                          <TableCell><strong>{instance.sku}</strong></TableCell>
                          <TableCell align="center">{instance.gpus}x {instance.gpu_type}</TableCell>
                          <TableCell align="center">{instance.gpu_memory_gb || '80'} GB</TableCell>
                          <TableCell align="center">{instance.tensor_performance || '312'} TFLOPS</TableCell>
                          <TableCell align="center">{instance.interconnect || 'NVLink'}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                            ${instance.price_per_gpu_hour.toFixed(3)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={instance.cost_advantage} color="success" size="small" />
                          </TableCell>
                        </TableRow>
                      )) || []}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* ML Performance Insights */}
            <Stack spacing={2}>
              <Alert severity="success">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  🤖 <strong>AI Acceleration:</strong> OCI provides up to 40% lower cost per GPU hour for H100 instances, 
                  with dedicated GPU clusters for large-scale ML training.
                </Typography>
              </Alert>
              <Alert severity="info">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  🔗 <strong>GPU Interconnect:</strong> OCI offers cluster networking up to 3.2 Tbps between GPU instances, 
                  enabling faster distributed training than AWS equivalents.
                </Typography>
              </Alert>
              <Alert severity="warning">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  📊 <strong>Model Serving:</strong> OCI's flexible shapes allow right-sizing GPU workloads, 
                  reducing costs for inference workloads by up to 60%.
                </Typography>
              </Alert>
            </Stack>
          </Box>
        )}

        {/* CDN & Edge Services Tab */}
        {activeResearchTab === 'cdn-edge' && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
              CDN & Edge Services Comparison
            </Typography>
            
            {/* CDN Services Comparison */}
            <Card elevation={2} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <PublicIcon sx={{ mr: 1, color: 'primary.main' }} />
                  Content Delivery Network Services
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Provider</strong></TableCell>
                        <TableCell><strong>Service</strong></TableCell>
                        <TableCell align="center"><strong>Global PoPs</strong></TableCell>
                        <TableCell align="center"><strong>Edge Locations</strong></TableCell>
                        <TableCell align="right"><strong>Data Transfer ($/GB)</strong></TableCell>
                        <TableCell align="right"><strong>Requests ($/10k)</strong></TableCell>
                        <TableCell align="center"><strong>Cost Advantage</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow hover sx={{ bgcolor: 'warning.50' }}>
                        <TableCell><Chip label="AWS" color="warning" size="small" /></TableCell>
                        <TableCell><strong>CloudFront</strong></TableCell>
                        <TableCell align="center">450+</TableCell>
                        <TableCell align="center">310+</TableCell>
                        <TableCell align="right">$0.085</TableCell>
                        <TableCell align="right">$0.0075</TableCell>
                        <TableCell align="center">-</TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50' }}>
                        <TableCell><Chip label="OCI" color="success" size="small" /></TableCell>
                        <TableCell><strong>CDN</strong></TableCell>
                        <TableCell align="center">140+</TableCell>
                        <TableCell align="center">60+</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>$0.05</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>$0.004</TableCell>
                        <TableCell align="center">
                          <Chip label="41% Savings" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Edge Computing Services */}
            <Card elevation={2} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <CloudIcon sx={{ mr: 1, color: 'primary.main' }} />
                  Edge Computing & Specialized Services
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Service Category</strong></TableCell>
                        <TableCell><strong>AWS Service</strong></TableCell>
                        <TableCell><strong>OCI Equivalent</strong></TableCell>
                        <TableCell align="right"><strong>AWS Cost</strong></TableCell>
                        <TableCell align="right"><strong>OCI Cost</strong></TableCell>
                        <TableCell align="center"><strong>Savings</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow hover>
                        <TableCell><strong>WAF & Security</strong></TableCell>
                        <TableCell>AWS WAF + Shield</TableCell>
                        <TableCell>OCI WAF + DDoS Protection</TableCell>
                        <TableCell align="right">$5-20/month</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>Included</TableCell>
                        <TableCell align="center">
                          <Chip label="100% Savings" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell><strong>Load Balancing</strong></TableCell>
                        <TableCell>Application Load Balancer</TableCell>
                        <TableCell>Load Balancer</TableCell>
                        <TableCell align="right">$16.20/month</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>$10.00/month</TableCell>
                        <TableCell align="center">
                          <Chip label="38% Savings" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell><strong>DNS Management</strong></TableCell>
                        <TableCell>Route 53</TableCell>
                        <TableCell>DNS</TableCell>
                        <TableCell align="right">$0.50/zone/month</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>$0.40/zone/month</TableCell>
                        <TableCell align="center">
                          <Chip label="20% Savings" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell><strong>Data Transfer</strong></TableCell>
                        <TableCell>CloudFront + Data Transfer</TableCell>
                        <TableCell>CDN + Free Regional Transfer</TableCell>
                        <TableCell align="right">$0.09/GB</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>$0.05/GB</TableCell>
                        <TableCell align="center">
                          <Chip label="44% Savings" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Edge Computing Insights */}
            <Stack spacing={2}>
              <Alert severity="success">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  🛡️ <strong>Security Included:</strong> OCI includes WAF and DDoS protection at no extra cost, 
                  while AWS charges $5-20+ per month for equivalent protection.
                </Typography>
              </Alert>
              <Alert severity="info">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  🌐 <strong>Regional Transfer:</strong> OCI provides free data transfer within the same region, 
                  reducing networking costs by up to 50% for multi-tier applications.
                </Typography>
              </Alert>
              <Alert severity="warning">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  🚀 <strong>Edge Performance:</strong> While AWS has more edge locations, OCI's strategic PoP placement 
                  delivers 95% coverage with 41% lower costs for most enterprise workloads.
                </Typography>
              </Alert>
            </Stack>
          </Box>
        )}

        {/* TCO Analysis Tab */}
        {activeResearchTab === 'tco' && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
              Enterprise 3-Year TCO Analysis
            </Typography>
            
            <Card elevation={2} sx={{ mb: 4, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.200' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'success.main', fontWeight: 700 }}>
                  💰 Enterprise Workload Scenario: 100 VMs, 500TB Storage, CDN, Databases
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mt: 3 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>AWS 3-Year Costs</Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Compute:</Typography>
                        <Typography>${performanceData.enterprise_tco_analysis.three_year_comparison.aws_total_cost.compute.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Storage:</Typography>
                        <Typography>${performanceData.enterprise_tco_analysis.three_year_comparison.aws_total_cost.storage.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Networking:</Typography>
                        <Typography>${performanceData.enterprise_tco_analysis.three_year_comparison.aws_total_cost.networking.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Data Transfer:</Typography>
                        <Typography>${performanceData.enterprise_tco_analysis.three_year_comparison.aws_total_cost.data_transfer.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'grey.300' }}>
                        <Typography sx={{ fontWeight: 'bold' }}>Total:</Typography>
                        <Typography sx={{ fontWeight: 'bold' }}>${performanceData.enterprise_tco_analysis.three_year_comparison.aws_total_cost.total.toLocaleString()}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom>OCI 3-Year Costs</Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Compute:</Typography>
                        <Typography sx={{ color: 'success.main', fontWeight: 'bold' }}>${performanceData.enterprise_tco_analysis.three_year_comparison.oci_total_cost.compute.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Storage:</Typography>
                        <Typography sx={{ color: 'success.main', fontWeight: 'bold' }}>${performanceData.enterprise_tco_analysis.three_year_comparison.oci_total_cost.storage.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Networking:</Typography>
                        <Typography sx={{ color: 'success.main', fontWeight: 'bold' }}>${performanceData.enterprise_tco_analysis.three_year_comparison.oci_total_cost.networking.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Data Transfer:</Typography>
                        <Typography sx={{ color: 'success.main', fontWeight: 'bold' }}>$0 (Free within region)</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'grey.300' }}>
                        <Typography sx={{ fontWeight: 'bold' }}>Total:</Typography>
                        <Typography sx={{ fontWeight: 'bold', color: 'success.main' }}>${performanceData.enterprise_tco_analysis.three_year_comparison.oci_total_cost.total.toLocaleString()}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
                <Box sx={{ mt: 3, p: 3, bgcolor: 'success.100', borderRadius: 2 }}>
                  <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold', textAlign: 'center' }}>
                    Total Savings: ${performanceData.enterprise_tco_analysis.three_year_comparison.oci_total_cost.savings.toLocaleString()} ({performanceData.enterprise_tco_analysis.three_year_comparison.oci_total_cost.savings_percentage})
                  </Typography>
                  <Typography variant="body1" sx={{ textAlign: 'center', mt: 1 }}>
                    ROI: {performanceData.enterprise_tco_analysis.migration_roi.roi_percentage}% | Payback Period: {performanceData.enterprise_tco_analysis.migration_roi.payback_period_months} months
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Research Methodology */}
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Enterprise Research Methodology & Validation</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Analysis based on SPEC CPU 2017 benchmarks, enterprise pricing (January 2025), 
              and real-world performance data from Oracle and AWS enterprise customers.
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip label="SPEC CPU 2017 Official Results" variant="outlined" />
              <Chip label="Enterprise Customer Benchmarks" variant="outlined" />
              <Chip label="Oracle Performance Engineering" variant="outlined" />
              <Chip label="Third-party TCO Analysis" variant="outlined" />
              <Chip label="Real Production Workloads" variant="outlined" />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderAnalysis = () => (
    <Box>
      {/* Executive Summary */}
      <Card elevation={2} sx={{ mb: 3, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.200' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 700 }}>
            <PsychologyIcon sx={{ mr: 1.5, fontSize: 32 }} />
            Executive Summary
          </Typography>
          <Typography variant="h6" sx={{ color: 'grey.800', lineHeight: 1.5, mb: 2 }}>
            {claude_analysis.executive_summary}
          </Typography>
          
          {claude_analysis.value_proposition && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Value Proposition</Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>💰 Cost Savings:</Typography>
                  <Typography variant="body2" color="text.secondary">{claude_analysis.value_proposition.cost_savings}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>⚡ Performance Gains:</Typography>
                  <Typography variant="body2" color="text.secondary">{claude_analysis.value_proposition.performance_gains}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>🔧 Operational Efficiency:</Typography>
                  <Typography variant="body2" color="text.secondary">{claude_analysis.value_proposition.operational_efficiency}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>🎯 Competitive Advantage:</Typography>
                  <Typography variant="body2" color="text.secondary">{claude_analysis.value_proposition.competitive_advantage}</Typography>
                </Box>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Key Differentiators */}
      {claude_analysis.key_differentiators && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              🏆 Oracle Cloud Advantages Your Competition Can't Match
            </Typography>
            <Stack spacing={3}>
              {claude_analysis.key_differentiators.map((diff: any, index: number) => (
                <Card key={index} variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ 
                      minWidth: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: 'success.main', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.2rem',
                      fontWeight: 'bold'
                    }}>
                      {index + 1}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" color="primary.main" gutterBottom sx={{ fontWeight: 600 }}>
                        {diff.title || diff.category}
                      </Typography>
                      <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
                        {diff.description}
                      </Typography>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 500, mb: 1 }}>
                        💼 Business Impact: {diff.business_impact}
                      </Typography>
                      {diff.customer_relevance && (
                        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
                          🎯 For Your Workloads: {diff.customer_relevance}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Service Recommendations */}
      {claude_analysis.service_recommendations && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              🎯 Strategic Migration Recommendations
            </Typography>
            <Stack spacing={2}>
              {claude_analysis.service_recommendations.map((rec: any, index: number) => (
                <Card key={index} variant="outlined" sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {rec.aws_service} → {rec.oci_recommendation || rec.recommendation}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {rec.business_case || rec.rationale}
                      </Typography>
                      {rec.unique_benefits && (
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                          ✨ Unique Benefits: {rec.unique_benefits}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ ml: 2, textAlign: 'right' }}>
                      <Chip 
                        label={`${rec.migration_priority} Priority`}
                        color={rec.migration_priority === 'High' ? 'error' : 
                               rec.migration_priority === 'Medium' ? 'warning' : 'success'}
                        sx={{ mb: 1 }}
                      />
                      {rec.current_monthly_cost && rec.projected_monthly_cost && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Current: {rec.current_monthly_cost}</Typography>
                          <Typography variant="caption" color="success.main" sx={{ display: 'block', fontWeight: 600 }}>
                            Projected: {rec.projected_monthly_cost}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Competitive Advantages */}
      {claude_analysis.competitive_advantages && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'error.main', fontWeight: 600 }}>
              ⚡ Why AWS Falls Short vs Oracle Cloud
            </Typography>
            <Stack spacing={2}>
              {claude_analysis.competitive_advantages.map((comp: any, index: number) => (
                <Box key={index} sx={{ p: 2, bgcolor: 'error.50', borderRadius: 2, border: '1px solid', borderColor: 'error.200' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main', mb: 1 }}>
                    ❌ AWS Limitation: {comp.aws_limitation}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main', mb: 1 }}>
                    ✅ Oracle Advantage: {comp.oci_advantage}
                  </Typography>
                  <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                    🎯 Your Benefit: {comp.customer_benefit}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Risk Mitigation */}
      {claude_analysis.risk_mitigation && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'warning.main', fontWeight: 600 }}>
              🛡️ Risk Mitigation & Objection Handling
            </Typography>
            <Stack spacing={2}>
              {claude_analysis.risk_mitigation.map((risk: any, index: number) => (
                <Box key={index} sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    🤔 Concern: {risk.concern}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main', mb: 1 }}>
                    💡 Oracle Solution: {risk.oracle_solution}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    📋 Proof Points: {risk.proof_points}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ROI Justification */}
      {claude_analysis.roi_justification && (
        <Card elevation={2} sx={{ mb: 3, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.200' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'success.main', fontWeight: 700 }}>
              💰 ROI Justification
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {claude_analysis.roi_justification}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Urgency Factors */}
      {claude_analysis.urgency_factors && (
        <Card elevation={2} sx={{ mb: 3, bgcolor: 'warning.50', border: '2px solid', borderColor: 'warning.300' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'warning.main', fontWeight: 600 }}>
              ⏰ Why You Should Act Now
            </Typography>
            <Stack spacing={1}>
              {claude_analysis.urgency_factors.map((factor: string, index: number) => (
                <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ color: 'warning.main', mr: 1, fontSize: '1.2rem' }}>🔥</Box>
                  {factor}
                </Typography>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Next Steps */}
      {claude_analysis.next_steps && (
        <Card elevation={2} sx={{ bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.200' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 700 }}>
              🚀 Next Steps to Get Started
            </Typography>
            <Box component="ol" sx={{ pl: 2 }}>
              {claude_analysis.next_steps.map((step: string, index: number) => (
                <Typography component="li" key={index} variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                  {step}
                </Typography>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>

      <Card sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            '& .MuiTabs-indicator': { height: 3 },
            '& .MuiTab-root': { 
              minHeight: 48,
              fontWeight: 500,
              fontSize: '0.8rem',
              minWidth: 80,
              padding: '6px 8px'
            }
          }}
        >
          <Tab 
            label="Overview" 
            value="overview" 
            icon={<AssessmentIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Mappings" 
            value="mappings" 
            icon={<MapIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Compute" 
            value="compute" 
            icon={<ComputerIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Networking" 
            value="networking" 
            icon={<NetworkCheckIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Storage" 
            value="storage" 
            icon={<StorageIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Performance" 
            value="performance" 
            icon={<SpeedIcon />}
            iconPosition="start"
          />
          <Tab 
            label="AI Insights" 
            value="analysis" 
            icon={<PsychologyIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Deck" 
            value="sales-deck" 
            icon={<SlideshowIcon />}
            iconPosition="start"
          />
          <Tab 
            label="BOM" 
            value="bom" 
            icon={<ReceiptIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Card>

      <Box sx={{ mb: 4 }}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'mappings' && renderServiceMappings()}
        {activeTab === 'bom' && renderBOM()}
        {activeTab === 'performance' && renderPerformanceAnalysis()}
        {activeTab === 'compute' && renderComputeAnalysis()}
        {activeTab === 'networking' && renderNetworkingAnalysis()}
        {activeTab === 'storage' && renderStorageAnalysis()}
        {activeTab === 'research' && renderResearchSection()}
        {activeTab === 'analysis' && renderAnalysis()}
        {activeTab === 'sales-deck' && renderSalesDeckCarousel()}
      </Box>


      {/* Sales Pitch Deck Modal */}
      {showSalesDeck && salesDeck && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          bgcolor: 'rgba(0,0,0,0.5)', 
          zIndex: 1300,
          overflow: 'auto',
          p: 2
        }}>
          <Box sx={{ 
            bgcolor: 'background.default', 
            borderRadius: 2, 
            maxWidth: 1400, 
            mx: 'auto', 
            my: 2,
            minHeight: 'calc(100vh - 32px)'
          }}>
            <SalesPitchDeck 
              salesDeck={salesDeck} 
              onClose={() => setShowSalesDeck(false)} 
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ComparisonDashboard;